import React, { useState, useEffect, useCallback, useLayoutEffect } from 'react';
import { API_URL } from '../constants';
import { resolverEstatusEmpleado } from '../utils/estatusEmpleado';
import { CLIENTE_C4B, FINALES, TURNOS, actualizarBorrador, aplicarRespuestaCambio, aplicarRespuestaLote, aplicarTurnoFila, celdaEditable, enviables, fijarPlatilloCelda,
  fijarTurnoCelda, leerBorrador, nuevoIdPedido, reconciliarCambio, reconciliarCeldas, sinTurno, solicitudCambio, solicitudLote } from './comedorPedidos';
import * as XLSX from 'xlsx';
import { bytesXls, construirRevision, etiquetaPeriodo, fechaPedido, libroIntellisis, nombreArchivoIntellisis, periodoDe, sumarDias } from './comedorIntellisis';
import { AREAS_COMEDOR, ESTADOS_QUE_SE_GUARDAN, ESTADOS_SIN_PROBLEMA, ETIQUETA_ESTADO_IMPORTACION, MENSAJE_NO_AUTORIZADO, activosAusentes, idPadronValido,
  leerArchivoPadron, nombrePersona, personasCaptura } from './comedorPadron';

// ── Helpers ───────────────────────────────────────────────────────────────────

// Lecturas por GET (sin payload); escrituras por POST con el payload en el body.
export function apiComedor(path, payload, params) {
  let url = `${API_URL}?path=${path}`;
  if (params) Object.entries(params).forEach(([k, v]) => { url += `&${k}=${encodeURIComponent(v)}`; });

  const options = {};
  if (payload) {
    options.method = 'POST';
    options.headers = { 'Content-Type': 'application/json' };
    options.body = JSON.stringify(payload);
  }

  return fetch(url, options).then(r => r.json());
}

// Escrituras del padrón: siempre por POST y con la sesión de Google del usuario; el backend la verifica.
export const escribirPadron = (usuario, ruta, cuerpo) => apiComedor(ruta, { ...cuerpo, idToken: usuario?.idToken || '' });

// Personas para la captura según la fuente activa del backend: { fuente, personas (seleccionables), padron (todas) }.
// Sin la ruta (backend anterior) o con fuente EMPLEADOS: los activos de api/empleados, como siempre.
export async function cargarPersonasComedor() {
  const p = await apiComedor('api/comedor/personas');
  if (p && p.success && p.fuente === 'PADRON') {
    if (!p.padron_disponible) throw new Error('El padrón de comedor no está disponible: ' + (p.motivo || 'revisa la hoja'));
    return { fuente: 'PADRON', personas: personasCaptura(p.personas), padron: p.personas || [] };
  }
  const d = await apiComedor('api/empleados');
  return { fuente: 'EMPLEADOS', personas: empleadosActivosComedor(d && d.empleados), padron: [] };
}

// Empleados seleccionables en Comedor: sólo activos. api/empleados entrega `Estatus`
// (con mayúscula); se resuelve con el helper compartido. El id se conserva tal cual
// llega, incluidos los ceros iniciales.
export function empleadosActivosComedor(empleados) {
  return (empleados || []).filter(e => resolverEstatusEmpleado(null, e).valor === 'Activo');
}

// ── Catálogo de platillos: reglas compartidas con el backend ──────────────────

// Nombre tal como se guarda: sin espacios en los extremos y con los internos colapsados.
export function normalizarNombrePlatillo(nombre) {
  return String(nombre === undefined || nombre === null ? '' : nombre).replace(/\s+/g, ' ').trim();
}

// Dos platillos tienen el mismo nombre si coinciden sin distinguir mayúsculas (los acentos sí distinguen).
export function claveNombrePlatillo(nombre) {
  return normalizarNombrePlatillo(nombre).toLowerCase();
}

// Precio capturado explícitamente: número >= 0 (0 es válido). null si está vacío, no es numérico o es negativo.
export function precioPlatilloValido(precio) {
  if (typeof precio === 'number') return Number.isFinite(precio) && precio >= 0 ? precio : null;
  if (typeof precio !== 'string' || !/^\d+(\.\d+)?$/.test(precio.trim())) return null;
  return parseFloat(precio);
}

// Ayuda de captura para el alta: el precio y el proveedor más frecuentes entre los platillos ACTIVOS.
// Sin platillos activos (o sin el dato) el campo queda vacío.
export function defaultsPlatillo(platillos) {
  const masFrecuente = valores => {
    const cuenta = new Map();
    valores.forEach(v => cuenta.set(v, (cuenta.get(v) || 0) + 1));
    let mejor = '', veces = 0;
    cuenta.forEach((n, v) => { if (n > veces) { mejor = v; veces = n; } });
    return mejor;
  };
  const activos = (platillos || []).filter(p => p.activo === true);
  return {
    precio: String(masFrecuente(activos.map(p => precioPlatilloValido(p.precio)).filter(v => v !== null))),
    proveedor: masFrecuente(activos.map(p => String(p.proveedor || '').trim()).filter(Boolean)),
  };
}

// Orden de presentación (no cambia la hoja): activos primero y después inactivos, cada grupo alfabético.
export function ordenarPlatillos(platillos) {
  return (platillos || []).slice().sort((a, b) =>
    (a.activo === b.activo ? 0 : (a.activo ? -1 : 1)) ||
    String(a.nombre).localeCompare(String(b.nombre), 'es', { sensitivity: 'base' }));
}

// Búsqueda sencilla por nombre o proveedor (contiene el texto, sin distinguir mayúsculas).
export function filtrarPlatillos(platillos, busqueda) {
  const q = String(busqueda || '').trim().toLowerCase();
  if (!q) return platillos || [];
  return (platillos || []).filter(p => String(p.nombre).toLowerCase().includes(q) || String(p.proveedor || '').toLowerCase().includes(q));
}

// Alta (sin id_platillo) o edición (con id_platillo) de un platillo.
export function guardarPlatillo(payload) {
  return apiComedor('api/comedor/platillo/guardar', payload);
}

// ── Menú Semana: reglas de la pantalla (C.3) ──────────────────────────────────

const ordenAlfabetico = lista => lista.slice().sort((a, b) => String(a.nombre).localeCompare(String(b.nombre), 'es', { sensitivity: 'base' }));

// Qué muestra el selector de una opción del menú.
//   platillos: los seleccionables (activos, sin el siempre disponible); todos: el catálogo completo;
//   recientesIds: ids usados recientemente, del más reciente al más antiguo.
// Sin texto: "Recientes" y después el resto en orden alfabético, sin repetir. Con texto: los que lo
// contienen (sin distinguir mayúsculas, sin coincidencias aproximadas) y, si ningún platillo —activo
// o inactivo— tiene ese nombre según la regla del catálogo, la propuesta de agregarlo.
export function seccionesSelector(platillos, recientesIds, busqueda, todos) {
  const lista = platillos || [];
  const clave = claveNombrePlatillo(busqueda);
  if (!clave) {
    const porId = new Map(lista.map(p => [p.id_platillo, p]));
    const recientes = [];
    (recientesIds || []).forEach(id => { const p = porId.get(id); if (p && !recientes.includes(p)) recientes.push(p); });
    return { recientes, resto: ordenAlfabetico(lista.filter(p => !recientes.includes(p))), coincidencias: null, agregar: '', inactivo: null, fijo: null };
  }
  const existente = (todos || lista).find(p => claveNombrePlatillo(p.nombre) === clave) || null;
  return {
    recientes: [], resto: [],
    coincidencias: ordenAlfabetico(lista.filter(p => claveNombrePlatillo(p.nombre).includes(clave))),
    agregar: existente ? '' : normalizarNombrePlatillo(busqueda),
    inactivo: existente && existente.activo === false ? existente : null,
    fijo: existente && existente.siempre_disponible ? existente : null,
  };
}

// Opciones de un día tal como se envían al backend: sólo número e id del platillo.
export function opcionesDiaMenu(dia) {
  return [1, 2, 3].filter(n => dia && dia[n] && dia[n].id_platillo).map(n => ({ numero: n, id_platillo: dia[n].id_platillo }));
}

// Días cuya programación difiere de la cargada (se comparan las opciones 1, 2 y 3 de cada día).
export function diasModificadosMenu(base, actual, fechas) {
  const firma = dia => [1, 2, 3].map(n => (dia && dia[n] && dia[n].id_platillo) || '').join('|');
  return (fechas || []).filter(f => firma((base || {})[f]) !== firma((actual || {})[f]));
}

// Pedidos ACTIVOS de platillos que el cambio retira del día (ya no están en ninguna de sus opciones).
// Devuelve [{ nombre, cantidad }]. Los cancelados no cuentan; agregar opciones no afecta a nadie.
export function pedidosAfectadosMenu(baseDia, actualDia, pedidosDelDia) {
  const ids = dia => opcionesDiaMenu(dia).map(o => String(o.id_platillo));
  const quedan = new Set(ids(actualDia));
  const retirados = new Set(ids(baseDia).filter(id => !quedan.has(id)));
  const cuenta = new Map();
  (pedidosDelDia || []).forEach(p => {
    if (p.estado !== 'activo' || !retirados.has(String(p.id_platillo))) return;
    cuenta.set(p.nombre_platillo, (cuenta.get(p.nombre_platillo) || 0) + 1);
  });
  return [...cuenta].map(([nombre, cantidad]) => ({ nombre, cantidad }));
}

const DIAS_LARGOS = ['lunes', 'martes', 'miércoles', 'jueves', 'viernes'];
// "martes 13"
export function etiquetaDiaMenu(fecha, indice) {
  return `${DIAS_LARGOS[indice]} ${parseInt(String(fecha).split('-')[2], 10)}`;
}
const enumerar = partes => (partes.length < 2 ? partes.join('') : partes.slice(0, -1).join(', ') + ' y ' + partes[partes.length - 1]);

function getLunes(d) {
  const date = new Date(d || Date.now());
  const dia = date.getDay();
  date.setDate(date.getDate() + (dia === 0 ? -6 : 1 - dia));
  date.setHours(0, 0, 0, 0);
  return date;
}

function toYMD(d) {
  const date = new Date(d);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function diasSemana(lunesYMD) {
  const lunes = new Date(lunesYMD + 'T12:00:00');
  return Array.from({ length: 5 }, (_, i) => {
    const d = new Date(lunes);
    d.setDate(lunes.getDate() + i);
    return toYMD(d);
  });
}

const MESES_CORTO = ['','Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
function fmtFechaCorta(ymd) {
  const [, m, d] = ymd.split('-');
  return `${parseInt(d)} ${MESES_CORTO[parseInt(m)]}`;
}

function semanaLabel(lunesYMD) {
  const dias = diasSemana(lunesYMD);
  return `${fmtFechaCorta(dias[0])} – ${fmtFechaCorta(dias[4])}`;
}

function descargarPDF(base64, filename) {
  const bytes = atob(base64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  const blob = new Blob([arr], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}

const DIAS_NOMBRES = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie'];

// Mapeo de abreviaturas de sucursales a nombres completos
const SUCURSALES_MAP = {
  'APO': 'Apodaca',
  'GDL': 'Guadalajara',
  'JZR': 'Juarez',
  'SPG': 'San Pedro',
  'MEX': 'Mexico',
};

function expandirSucursal(abrev) {
  if (!abrev) return '';
  return SUCURSALES_MAP[String(abrev).toUpperCase()] || abrev;
}

// El backend devuelve menu[fecha] como { opciones: {1:{id_platillo,nombre}, 2:{...}, 3:{...}}, guarnicion_1, guarnicion_2 }
// Esta función lo normaliza al array [{numero, id_platillo, nombre_platillo, guarnicion_1, guarnicion_2}] que usan los componentes.
function normalizarMenuDia(diaData) {
  if (Array.isArray(diaData)) return diaData;
  if (!diaData || typeof diaData !== 'object') return [];
  const opciones = diaData.opciones || {};
  const g1 = diaData.guarnicion_1 || '';
  const g2 = diaData.guarnicion_2 || '';
  return [1, 2, 3]
    .filter(n => opciones[n] && opciones[n].id_platillo)
    .map(n => ({ numero: n, id_platillo: opciones[n].id_platillo, nombre_platillo: opciones[n].nombre, guarnicion_1: g1, guarnicion_2: g2 }));
}

// ── Estilos base ──────────────────────────────────────────────────────────────

const S = {
  card:    { background: 'white', borderRadius: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', padding: '20px' },
  th:      { padding: '10px 14px', textAlign: 'left', fontWeight: 600, fontSize: '12px', color: '#4b5563', borderBottom: '2px solid #e5e7eb' },
  td:      { padding: '10px 14px', fontSize: '13px', color: '#1f2937', borderBottom: '1px solid #f3f4f6' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal:   { background: 'white', borderRadius: '16px', padding: '28px', width: '520px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
  input:   { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box' },
  label:   { fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' },
  btn:     { padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 500 },
  // Botones de acciones
  btnCancel:  { padding: '6px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '11px', fontWeight: 500, background: '#fee2e2', color: '#dc2626', display: 'flex', alignItems: 'center', gap: '4px' },
  btnEdit:    { padding: '6px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '11px', fontWeight: 500, background: '#dbeafe', color: '#0369a1', display: 'flex', alignItems: 'center', gap: '4px' },
  btnDelete:  { padding: '6px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '11px', fontWeight: 500, background: '#f5d4d4', color: '#8b2e2e', display: 'flex', alignItems: 'center', gap: '4px' },
  btnView:    { padding: '6px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '11px', fontWeight: 500, background: '#e0e7ff', color: '#4c1d95', display: 'flex', alignItems: 'center', gap: '4px' },
  btnSuccess: { padding: '6px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '11px', fontWeight: 500, background: '#dcfce7', color: '#166534', display: 'flex', alignItems: 'center', gap: '4px' },
};

// ── Modal Platillo ────────────────────────────────────────────────────────────

export function ModalPlatillo({ platillo, defaults, nombreInicial, onClose, onGuardar }) {
  const esEdicion = !!platillo;
  const siempreDisponible = !!platillo?.siempre_disponible;
  const [form, setForm] = useState({
    nombre:      platillo?.nombre      || nombreInicial || '',
    descripcion: platillo?.descripcion || '',
    precio:      esEdicion ? String(platillo.precio ?? '') : (defaults?.precio || ''),
    proveedor:   esEdicion ? (platillo.proveedor || '') : (defaults?.proveedor || ''),
    activo:      platillo?.activo !== false,
  });
  const [guardando, setGuardando] = useState(false);
  const [err, setErr] = useState('');

  const handle = e => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleGuardar = async () => {
    const nombre = normalizarNombrePlatillo(form.nombre);
    if (!nombre) { setErr('El nombre es obligatorio.'); return; }
    const precio = precioPlatilloValido(String(form.precio));
    if (precio === null) { setErr('Escribe el precio: un número mayor o igual a 0.'); return; }
    setGuardando(true); setErr('');
    const payload = { nombre, descripcion: form.descripcion.trim(), precio, proveedor: form.proveedor.trim() };
    if (esEdicion) payload.id_platillo = platillo.id_platillo;
    // El platillo siempre disponible no se desactiva desde aquí.
    if (esEdicion && !siempreDisponible) payload.activo = form.activo;
    try {
      const data = await guardarPlatillo(payload);
      if (data.success) { onGuardar(data.platillo || { ...payload, id_platillo: data.id_platillo, siempre_disponible: !!platillo?.siempre_disponible, activo: payload.activo !== false }); onClose(); }
      else setErr(data.error || 'Error al guardar.');
    } catch {
      setErr('Error de conexión. Intenta de nuevo.');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div style={S.overlay}>
      <div style={S.modal}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 700, color: '#1d3d6b' }}>{esEdicion ? 'Editar Platillo' : 'Nuevo Platillo'}</h2>
          <button onClick={onClose} style={{ ...S.btn, background: 'none', color: '#6b7280', padding: '4px 8px' }}>✕</button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div>
            <label style={S.label}>Nombre *</label>
            <input style={S.input} name="nombre" value={form.nombre} onChange={handle} placeholder="Ej. Pollo en salsa" />
          </div>
          <div>
            <label style={S.label}>Descripción</label>
            <input style={S.input} name="descripcion" value={form.descripcion} onChange={handle} placeholder="Descripción opcional" />
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <div style={{ flex: 1 }}>
              <label style={S.label}>Precio ($) *</label>
              <input style={S.input} name="precio" type="number" min="0" step="0.01" value={form.precio} onChange={handle} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={S.label}>Proveedor</label>
              <input style={S.input} name="proveedor" value={form.proveedor} onChange={handle} />
            </div>
          </div>
          {esEdicion && !siempreDisponible && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.activo} onChange={e => setForm(p => ({ ...p, activo: e.target.checked }))} />
              Activo
            </label>
          )}
        </div>
        {err && <div style={{ marginTop: '12px', padding: '8px 12px', background: '#fef2f2', color: '#dc2626', borderRadius: '6px', fontSize: '13px' }}>⚠️ {err}</div>}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
          <button onClick={onClose} style={{ ...S.btn, background: '#f3f4f6', color: '#374151' }}>Cancelar</button>
          <button onClick={handleGuardar} disabled={guardando}
            style={{ ...S.btn, background: '#0086ab', color: 'white', opacity: guardando ? 0.7 : 1 }}>
            {guardando ? 'Guardando...' : (esEdicion ? 'Guardar cambios' : 'Crear platillo')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Tab 1: Catálogo ───────────────────────────────────────────────────────────

export function TabCatalogo() {
  const [platillos, setPlatillos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [msg, setMsg] = useState(null);
  const [busqueda, setBusqueda] = useState('');

  const cargar = useCallback(async () => {
    setLoading(true);
    const data = await apiComedor('api/comedor/platillos');
    if (data.success) setPlatillos(data.platillos || []);
    setLoading(false);
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const mostrarMsg = (texto, tipo = 'ok') => {
    setMsg({ texto, tipo });
    setTimeout(() => setMsg(null), 3000);
  };

  const visibles = ordenarPlatillos(filtrarPlatillos(platillos, busqueda));

  return (
    <div style={{ padding: '24px', maxWidth: '900px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#1d3d6b' }}>Catálogo de Platillos</h2>
          <p style={{ color: '#6b7280', fontSize: '13px', marginTop: '2px' }}>Platillos disponibles para el menú</p>
        </div>
        <button onClick={() => setModal('nuevo')}
          style={{ ...S.btn, background: '#0086ab', color: 'white', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <i className="ti ti-plus"></i> Nuevo Platillo
        </button>
      </div>

      {msg && (
        <div style={{ marginBottom: '16px', padding: '10px 14px', borderRadius: '8px', fontSize: '13px',
          background: msg.tipo === 'err' ? '#fef2f2' : '#f0fdf4',
          color: msg.tipo === 'err' ? '#dc2626' : '#166534',
          border: `1px solid ${msg.tipo === 'err' ? '#fecaca' : '#bbf7d0'}` }}>
          {msg.tipo === 'err' ? '⚠️' : '✅'} {msg.texto}
        </div>
      )}

      <input style={{ ...S.input, marginBottom: '12px' }} value={busqueda} onChange={e => setBusqueda(e.target.value)}
        placeholder="Buscar por nombre o proveedor..." aria-label="Buscar platillo" />

      <div style={{ ...S.card, padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>Cargando platillos...</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#f9fafb' }}>
              <tr>
                <th style={S.th}>Nombre</th>
                <th style={S.th}>Proveedor</th>
                <th style={S.th}>Precio</th>
                <th style={S.th}>Estado</th>
              </tr>
            </thead>
            <tbody>
              {visibles.length === 0 ? (
                <tr><td colSpan={4} style={{ ...S.td, textAlign: 'center', color: '#9ca3af', padding: '40px' }}>
                  {platillos.length === 0 ? 'Sin platillos registrados' : 'Ningún platillo coincide con la búsqueda'}
                </td></tr>
              ) : visibles.map(p => (
                <tr key={p.id_platillo}
                  onClick={() => setModal(p)}
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#f9fafb'; }}
                  onMouseLeave={e => e.currentTarget.style.background = ''}>
                  <td style={S.td}>
                    <div style={{ fontWeight: 600 }}>
                      {p.nombre}
                      {p.siempre_disponible && <span style={{ marginLeft: '8px', background: '#eff6ff', color: '#1d4ed8', padding: '1px 8px', borderRadius: '99px', fontSize: '10px', fontWeight: 600 }}>Siempre disponible</span>}
                    </div>
                    {p.descripcion && <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>{p.descripcion}</div>}
                  </td>
                  <td style={S.td}>{p.proveedor || '—'}</td>
                  <td style={S.td}>${parseFloat(p.precio || 0).toFixed(2)}</td>
                  <td style={S.td}>
                    <span style={{ background: p.activo ? '#dcfce7' : '#f3f4f6', color: p.activo ? '#166534' : '#6b7280', padding: '2px 10px', borderRadius: '99px', fontSize: '11px', fontWeight: 600 }}>
                      {p.activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal && (
        <ModalPlatillo
          platillo={modal === 'nuevo' ? null : modal}
          defaults={defaultsPlatillo(platillos)}
          onClose={() => setModal(null)}
          onGuardar={() => { cargar(); mostrarMsg(modal === 'nuevo' ? 'Platillo creado.' : 'Platillo actualizado.'); }}
        />
      )}
    </div>
  );
}

// ── Placeholders (Tasks 2–6) ──────────────────────────────────────────────────

// ── Stubs para modales y grid (implementados en Tasks 3 y 4) ─────────────────

function ModalAvisarCocina({ fecha, onClose }) {
  const [texto, setTexto] = useState('');
  const [loading, setLoading] = useState(true);
  const [copiado, setCopiado] = useState(false);

  useEffect(() => {
    apiComedor('api/comedor/whatsapp-texto', null, { fecha }).then(data => {
      setTexto(data.success ? (data.texto || '') : 'Error al cargar el texto.');
      setLoading(false);
    });
  }, [fecha]);

  const handleCopiar = async () => {
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      alert('No se pudo copiar. Selecciona el texto manualmente.');
    }
  };

  return (
    <div style={S.overlay}>
      <div style={{ ...S.modal, width: '480px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ fontSize: '17px', fontWeight: 700, color: '#1d3d6b' }}>
            <i className="ti ti-brand-whatsapp" style={{ marginRight: '8px', color: '#25d366' }}></i>
            Avisar a Cocina
          </h2>
          <button onClick={onClose} style={{ ...S.btn, background: 'none', color: '#6b7280', padding: '4px 8px' }}>✕</button>
        </div>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>Cargando texto...</div>
        ) : (
          <>
            <textarea readOnly value={texto} rows={12}
              style={{ ...S.input, fontFamily: 'monospace', fontSize: '12px', resize: 'vertical', lineHeight: '1.6' }} />
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '14px' }}>
              <button onClick={onClose} style={{ ...S.btn, background: '#f3f4f6', color: '#374151' }}>Cerrar</button>
              <button onClick={handleCopiar}
                style={{ ...S.btn, background: copiado ? '#16a34a' : '#25d366', color: 'white', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <i className={`ti ${copiado ? 'ti-check' : 'ti-copy'}`}></i>
                {copiado ? '¡Copiado!' : 'Copiar'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── C.4b: captura unificada de pedidos ─────────────────────────────────────────
// «Registrar» y «Carga semanal» son la misma captura (una fila o varias: empleado × días de la semana). Cada celda
// lleva un id_pedido estable y vive en el borrador de sesión hasta quedar confirmada (ver comedorPedidos.js).
const ETIQUETA_ESTADO = { enviando: 'Enviando…', incierto: 'Sin confirmar', guardado: 'Guardado', ya_existia: 'Ya estaba', error: 'No se guardó', revision: 'Revisar' };
const COLOR_ESTADO = { pendiente: '#0086ab', enviando: '#6b7280', incierto: '#d97706', guardado: '#16a34a', ya_existia: '#16a34a', error: '#dc2626', revision: '#b91c1c' };

function opcionDeCodigo(menuDia, ensalada, codigo) {
  if (codigo === 'E') return ensalada ? { id_platillo: String(ensalada.id_platillo), nombre_platillo: ensalada.nombre } : null;
  const o = normalizarMenuDia(menuDia).find(m => String(m.numero) === codigo);
  return o ? { id_platillo: String(o.id_platillo), nombre_platillo: o.nombre_platillo } : null;
}
const clavePedido = (idEmpleado, fecha) => String(idEmpleado) + '|' + fecha;

export function CapturaPedidos({ usuario, modo, semanaInicial, borrador, onCerrar, onTerminado }) {
  const email = usuario?.email || '';
  const modoCaptura = borrador?.modo || modo;
  const [semana, setSemana] = useState(borrador?.semana || semanaInicial);
  // Un borrador restaurado con pedidos «enviando» quedó sin respuesta: pasan a «incierto» (mismos IDs).
  const [filas, setFilas] = useState(() => (borrador?.filas || []).map(f => ({ ...f, celdas: Object.fromEntries(Object.entries(f.celdas || {}).map(([k, c]) => [k, c.estado === 'enviando' ? { ...c, estado: 'incierto' } : c])) })));
  const [menuSemana, setMenuSemana] = useState({});
  const [ensalada, setEnsalada] = useState(null);
  const [guardados, setGuardados] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [fuente, setFuente] = useState('');
  const [padron, setPadron] = useState([]);
  const [personaModal, setPersonaModal] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [ocupado, setOcupado] = useState(borrador ? 'Verificando qué quedó guardado…' : '');
  const [aviso, setAviso] = useState(null);
  const diasSem = diasSemana(semana);
  const celdas = filas.flatMap(f => Object.values(f.celdas || {}));
  const hayCeldas = celdas.length > 0;

  const persistir = useCallback(nuevas => {
    setFilas(nuevas);
    const pendientes = nuevas.flatMap(f => Object.values(f.celdas || {})).some(c => !FINALES.includes(c.estado));
    actualizarBorrador(email, { captura: pendientes || nuevas.length ? { modo: modoCaptura, semana, filas: nuevas } : null });
  }, [email, modoCaptura, semana]);

  const leerGuardados = useCallback(async () => {
    const d = await apiComedor('api/comedor/pedidos-semana', null, { semana_inicio: semana });
    if (!d || !d.success) throw new Error((d && d.error) || 'No se pudo consultar lo guardado');
    setGuardados(d.pedidos || []);
    return d.pedidos || [];
  }, [semana]);

  useEffect(() => {
    apiComedor('api/comedor/menu/semana', null, { semana_inicio: semana }).then(d => { if (d.success) { setMenuSemana(d.menu || {}); setEnsalada(d.ensalada || null); } }).catch(() => {});
    if (!borrador) leerGuardados().catch(() => {});
  }, [semana, borrador, leerGuardados]);

  useEffect(() => {
    cargarPersonasComedor().then(r => { setFuente(r.fuente); setEmpleados(r.personas); setPadron(r.padron); })
      .catch(e => { setFuente('ERROR'); setAviso({ tipo: 'error', texto: 'No se pudo cargar la lista de personas: ' + e.message }); });
  }, []);

  // Al restaurar un borrador, ANTES de nada se consulta qué quedó guardado y se reconcilia.
  const reconciliar = useCallback(async () => {
    setOcupado('Verificando qué quedó guardado…');
    try {
      const g = await leerGuardados();
      setFilas(prev => {
        const nuevas = prev.map(f => {
          const c = reconciliarCeldas(Object.values(f.celdas || {}), g);
          return { ...f, celdas: Object.fromEntries(c.map(x => [x.fecha, x])) };
        });
        persistirSinEstado(nuevas);
        return nuevas;
      });
      setAviso({ tipo: 'info', texto: 'Se verificó lo guardado: lo confirmado quedó marcado; lo demás conserva su mismo ID.' });
    } catch (e) {
      setAviso({ tipo: 'error', texto: 'No se pudo verificar lo guardado (' + e.message + '). Nada se modificó; vuelve a intentar.' });
    }
    setOcupado('');
  }, [leerGuardados]); // eslint-disable-line react-hooks/exhaustive-deps
  const persistirSinEstado = nuevas => {
    const pendientes = nuevas.flatMap(f => Object.values(f.celdas || {})).some(c => !FINALES.includes(c.estado));
    actualizarBorrador(email, { captura: pendientes || nuevas.length ? { modo: modoCaptura, semana, filas: nuevas } : null });
  };
  useEffect(() => { if (borrador) reconciliar(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtrados = busqueda.length > 1
    ? empleados.filter(e => ((e.nombre || '').toLowerCase().includes(busqueda.toLowerCase()) || String(e.id || '').includes(busqueda)) && !filas.find(f => String(f.id_empleado) === String(e.id))).slice(0, 8)
    : [];
  const puedeAgregar = modoCaptura === 'semanal' || filas.length === 0;

  const agregarEmpleado = e => {
    // Con el padrón, una persona sin destino de entrega lo completa ANTES de registrar su comida.
    if (fuente === 'PADRON' && !e.destino) { setPersonaModal({ modo: 'destino', persona: padron.find(p => p.id_empleado === String(e.id)) }); return; }
    persistir([...filas, { id_empleado: String(e.id), nombre: e.nombre, sucursal: e.sucursal, destino: e.destino, turno: '', celdas: {} }]); setBusqueda('');
  };
  // Persona creada, encontrada o completada en el padrón: se incorpora a la búsqueda y queda seleccionada.
  const personaLista = p => {
    setPadron(prev => [...prev.filter(x => x.id_empleado !== p.id_empleado), p]);
    setEmpleados(prev => [...prev.filter(x => String(x.id) !== p.id_empleado), ...personasCaptura([p])]);
    setPersonaModal(null);
    if (!filas.find(f => String(f.id_empleado) === p.id_empleado)) agregarEmpleado({ id: p.id_empleado, nombre: p.nombre_completo, destino: p.area_comedor });
  };
  const busq = busqueda.trim();
  const deshabilitada = fuente === 'PADRON' && busq.length > 1 && !filtrados.length ? padron.find(p => !p.habilitado && (p.id_empleado === busq || p.nombre_completo.toLowerCase().includes(busq.toLowerCase()))) : null;

  const setCodigo = (filaIdx, fecha, raw) => {
    const codigo = String(raw || '').toUpperCase().slice(-1);
    if (codigo && !['1', '2', '3', 'E'].includes(codigo)) return;
    const fila = filas[filaIdx], actual = fila.celdas[fecha];
    if (actual && !celdaEditable(actual)) {
      setAviso({ tipo: 'error', texto: actual.estado === 'incierto' ? 'Ese pedido está sin confirmar: verifica o reintenta antes de cambiarlo.' : 'Ese pedido ya no se puede editar desde la captura.' });
      return;
    }
    const celdasFila = { ...fila.celdas };
    if (!codigo) delete celdasFila[fecha];
    else {
      const op = opcionDeCodigo(menuSemana[fecha], ensalada, codigo);
      if (!op) { setAviso({ tipo: 'error', texto: `Sin opción ${codigo} el ${fmtFechaCorta(fecha)}: programa el menú primero.` }); return; }
      const nueva = fijarPlatilloCelda(actual, { id_empleado: fila.id_empleado, nombre: fila.nombre, fecha, id_platillo: op.id_platillo, nombre_platillo: op.nombre_platillo });
      // El turno de una celda nueva es el del renglón; si el día ya tenía uno propio, se conserva.
      celdasFila[fecha] = { ...nueva, opcion: codigo, turno: (actual && actual.turno) || fila.turno || '', turnoPropio: !!(actual && actual.turnoPropio) };
    }
    setAviso(null);
    persistir(filas.map((f, i) => (i === filaIdx ? { ...f, celdas: celdasFila } : f)));
  };

  // Turno del renglón: se aplica a los días de ESTA captura que no tienen turno propio (nunca a pedidos ya guardados).
  const cambiarTurnoFila = (filaIdx, turno) =>
    persistir(filas.map((f, i) => (i === filaIdx ? { ...f, turno, celdas: aplicarTurnoFila(f.celdas, turno) } : f)));
  // Turno de un día: queda como propio si es distinto del renglón.
  const cambiarTurnoCelda = (filaIdx, fecha, turno) => {
    const fila = filas[filaIdx];
    const c = fijarTurnoCelda(fila.celdas[fecha], turno, turno !== fila.turno);
    persistir(filas.map((f, i) => (i === filaIdx ? { ...f, celdas: { ...f.celdas, [fecha]: c } } : f)));
  };

  const quitarCelda = (filaIdx, fecha) => {
    const celdasFila = { ...filas[filaIdx].celdas }; delete celdasFila[fecha];
    persistir(filas.map((f, i) => (i === filaIdx ? { ...f, celdas: celdasFila } : f)));
  };
  const quitarFila = filaIdx => persistir(filas.filter((_, i) => i !== filaIdx));
  const filaRetirable = f => !Object.values(f.celdas || {}).some(c => c.estado === 'enviando' || c.estado === 'incierto');

  const guardar = async () => {
    const env = enviables(celdas);
    if (!env.length) return;
    const faltan = sinTurno(celdas);
    if (faltan.length) { setAviso({ tipo: 'error', texto: `Elige el turno de ${faltan.length} pedido${faltan.length !== 1 ? 's' : ''} antes de guardar (${TURNOS.join(', ')}).` }); return; }
    const claves = new Set(env.map(c => clavePedido(c.id_empleado, c.fecha)));
    const marcar = (lista, f) => lista.map(fl => ({ ...fl, celdas: Object.fromEntries(Object.entries(fl.celdas).map(([k, c]) => [k, claves.has(clavePedido(c.id_empleado, c.fecha)) ? f(c) : c])) }));
    const enviando = marcar(filas, c => ({ ...c, estado: 'enviando' }));
    persistir(enviando);   // el borrador ya tiene los IDs ANTES de enviar
    setOcupado('Guardando…'); setAviso(null);
    let data = null;
    try { data = await apiComedor('api/comedor/pedido/crear-bulk', solicitudLote(env)); } catch (e) { data = null; }
    const resultado = aplicarRespuestaLote(env, data);
    const porClave = Object.fromEntries(resultado.map(c => [clavePedido(c.id_empleado, c.fecha), c]));
    const nuevas = marcar(enviando, c => porClave[clavePedido(c.id_empleado, c.fecha)]);
    const todas = nuevas.flatMap(f => Object.values(f.celdas));
    setOcupado('');
    if (todas.length && todas.every(c => FINALES.includes(c.estado))) {
      actualizarBorrador(email, { captura: null });
      setFilas(nuevas);
      onTerminado(`${todas.length} pedido${todas.length !== 1 ? 's' : ''} registrado${todas.length !== 1 ? 's' : ''}.`);
      return;
    }
    persistir(nuevas);
    const incierto = resultado.some(c => c.estado === 'incierto');
    setAviso({ tipo: 'error', texto: data && data.error ? data.error : (incierto ? 'Sin respuesta del servidor. Puedes reintentar (se envían los mismos pedidos) o verificar qué quedó guardado.' : 'Hay pedidos sin guardar.') });
  };

  const descartar = () => { actualizarBorrador(email, { captura: null }); onCerrar(); };
  const hayIncierto = celdas.some(c => c.estado === 'incierto' || c.estado === 'enviando');
  const nEnviables = enviables(celdas).length;
  const moverSemana = dir => {
    if (hayCeldas) return;
    const d = new Date(semana + 'T12:00:00'); d.setDate(d.getDate() + dir * 7); setSemana(toYMD(getLunes(d)));
  };
  const leyenda = Object.fromEntries(diasSem.map(f => [f, normalizarMenuDia(menuSemana[f])]));

  return (
    <div style={{ padding: '24px' }} aria-label="Captura de pedidos">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', gap: '12px', flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#1d3d6b' }}>{modoCaptura === 'semanal' ? 'Carga semanal' : 'Registrar pedido'}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
            <button onClick={() => moverSemana(-1)} disabled={hayCeldas} title={hayCeldas ? 'La semana se fija al capturar' : 'Semana anterior'} style={{ ...S.btn, background: '#f3f4f6', color: '#374151', padding: '4px 10px', opacity: hayCeldas ? 0.4 : 1 }}>‹</button>
            <span style={{ color: '#6b7280', fontSize: '13px' }}>Semana {semanaLabel(semana)}</span>
            <button onClick={() => moverSemana(1)} disabled={hayCeldas} title={hayCeldas ? 'La semana se fija al capturar' : 'Semana siguiente'} style={{ ...S.btn, background: '#f3f4f6', color: '#374151', padding: '4px 10px', opacity: hayCeldas ? 0.4 : 1 }}>›</button>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={onCerrar} disabled={!!ocupado} style={{ ...S.btn, background: '#f3f4f6', color: '#374151' }}>{hayCeldas ? 'Cerrar (conservar borrador)' : 'Cerrar'}</button>
          {hayCeldas && <button onClick={descartar} disabled={hayIncierto || !!ocupado} title={hayIncierto ? 'Verifica primero lo que quedó sin confirmar' : 'Descartar esta captura'}
            style={{ ...S.btn, background: '#fee2e2', color: '#b91c1c', opacity: hayIncierto ? 0.5 : 1 }}>Descartar captura</button>}
        </div>
      </div>

      <div style={{ ...S.card, marginBottom: '16px', padding: '12px 16px', overflowX: 'auto' }}>
        <div style={{ fontSize: '11px', fontWeight: 600, color: '#6b7280', marginBottom: '8px', textTransform: 'uppercase' }}>Menú de la semana · escribe 1, 2, 3 o E</div>
        <table style={{ borderCollapse: 'collapse', fontSize: '12px', whiteSpace: 'nowrap' }}>
          <thead><tr><th style={{ padding: '4px 12px', textAlign: 'left', color: '#9ca3af' }}>Op.</th>
            {diasSem.map((f, i) => <th key={f} style={{ padding: '4px 12px', color: '#1d3d6b' }}>{DIAS_NOMBRES[i]} <span style={{ fontWeight: 400, color: '#9ca3af' }}>{fmtFechaCorta(f)}</span></th>)}</tr></thead>
          <tbody>{['1', '2', '3', 'E'].map(op => (
            <tr key={op}><td style={{ padding: '4px 12px', fontWeight: 700, color: '#0086ab' }}>{op}</td>
              {diasSem.map(f => <td key={f} style={{ padding: '4px 12px', textAlign: 'center' }}>{op === 'E' ? (ensalada?.nombre || '—') : (leyenda[f].find(m => String(m.numero) === op)?.nombre_platillo || '—')}</td>)}</tr>
          ))}</tbody>
        </table>
      </div>

      {puedeAgregar && (
        <div style={{ position: 'relative', marginBottom: '16px', maxWidth: '360px' }}>
          <input style={S.input} value={busqueda} onChange={e => setBusqueda(e.target.value)} placeholder={fuente === 'PADRON' ? 'Agregar persona del padrón por nombre o ID...' : 'Agregar empleado por nombre o número...'} aria-label="Buscar empleado" />
          {fuente === 'PADRON' && <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>Fuente: padrón temporal de comedor</div>}
          {filtrados.length > 0 && (
            <div style={{ position: 'absolute', zIndex: 10, top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
              {filtrados.map(e => (
                <div key={e.id} role="option" aria-selected="false" onClick={() => agregarEmpleado(e)} style={{ padding: '9px 14px', cursor: 'pointer', fontSize: '13px', borderBottom: '1px solid #f3f4f6' }}>
                  {e.nombre} <span style={{ fontSize: '11px', color: '#9ca3af' }}>#{e.id} · {fuente === 'PADRON' ? (e.destino ? 'Entrega ' + e.destino : 'Sin destino de entrega') : expandirSucursal(e.sucursal)}</span>
                </div>
              ))}
            </div>
          )}
          {fuente === 'PADRON' && busq.length > 1 && !filtrados.length && (
            <div data-testid="sin-resultados" style={{ marginTop: '8px', fontSize: '12px', color: '#374151' }}>
              {deshabilitada ? <span style={{ color: '#b91c1c' }}>#{deshabilitada.id_empleado} {deshabilitada.nombre_completo} está deshabilitada en el padrón: no puede registrar comidas nuevas.</span>
                : <>No está en el padrón. <button onClick={() => setPersonaModal({ modo: 'alta', inicial: /^\d+$/.test(busq) ? { id_empleado: busq } : { nombre_completo: busq } })} style={{ ...S.btn, padding: '4px 10px', background: '#0086ab', color: 'white', marginLeft: '6px' }}>Agregar persona</button></>}
            </div>
          )}
        </div>
      )}
      {personaModal && <ModalPersonaPadron usuario={usuario} {...personaModal} onCerrar={() => setPersonaModal(null)} onLista={personaLista} />}

      {filas.length > 0 && (
        <div style={{ ...S.card, padding: 0, overflowX: 'auto', marginBottom: '16px' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead style={{ background: '#f9fafb' }}><tr>
              <th style={{ ...S.th, minWidth: '180px' }}>Empleado</th>
              {diasSem.map((f, i) => <th key={f} style={{ ...S.th, textAlign: 'center', minWidth: '110px' }}>{DIAS_NOMBRES[i]}<br /><span style={{ fontSize: '10px', fontWeight: 400, color: '#9ca3af' }}>{fmtFechaCorta(f)}</span></th>)}
              <th style={{ ...S.th, width: '36px' }}></th>
            </tr></thead>
            <tbody>
              {filas.map((fila, filaIdx) => (
                <tr key={fila.id_empleado}>
                  <td style={{ ...S.td, fontWeight: 500 }}>{fila.nombre}<span style={{ fontSize: '11px', color: '#9ca3af', display: 'block' }}>#{fila.id_empleado}{fila.destino ? ' · Entrega ' + fila.destino : ''}</span>
                    <select value={fila.turno || ''} onChange={e => cambiarTurnoFila(filaIdx, e.target.value)} disabled={!!ocupado} aria-label={`Turno de ${fila.nombre}`}
                      title="Se aplica a los días que captures en este renglón; cada día se puede ajustar"
                      style={{ marginTop: '4px', fontSize: '12px', padding: '2px 4px', borderRadius: '4px', border: `1px solid ${fila.turno ? '#d1d5db' : '#f59e0b'}` }}>
                      <option value="">Turno…</option>
                      {TURNOS.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </td>
                  {diasSem.map(fecha => {
                    const c = fila.celdas[fecha];
                    const yaTiene = guardados.find(p => p.estado === 'activo' && String(p.fecha_entrega).slice(0, 10) === fecha && String(p.id_empleado).padStart(5, '0') === String(fila.id_empleado).padStart(5, '0') && (!c || p.id_pedido !== c.id_pedido));
                    const editable = celdaEditable(c);
                    return (
                      <td key={fecha} style={{ ...S.td, textAlign: 'center', padding: '6px', verticalAlign: 'top' }} data-celda={fila.id_empleado + '|' + fecha}>
                        <input maxLength={2} value={c ? (c.opcion || '?') : ''} disabled={!editable || !!ocupado} aria-label={`Opción de ${fila.nombre} el ${fmtFechaCorta(fecha)}`}
                          onChange={e => setCodigo(filaIdx, fecha, e.target.value)}
                          style={{ width: '38px', height: '38px', textAlign: 'center', fontWeight: 700, fontSize: '16px', textTransform: 'uppercase', outline: 'none',
                            border: `2px solid ${c ? COLOR_ESTADO[c.estado] : '#e5e7eb'}`, borderRadius: '6px', background: editable ? 'white' : '#f9fafb' }} />
                        {c && <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '2px' }}>{c.nombre_platillo}</div>}
                        {c && (
                          <select value={c.turno || ''} onChange={e => cambiarTurnoCelda(filaIdx, fecha, e.target.value)} disabled={!editable || !!ocupado}
                            aria-label={`Turno de ${fila.nombre} el ${fmtFechaCorta(fecha)}`} data-turno-propio={c.turnoPropio ? 'si' : undefined}
                            style={{ marginTop: '2px', fontSize: '10px', padding: '1px 2px', borderRadius: '4px', fontWeight: c.turnoPropio ? 700 : 400,
                              border: `1px solid ${!c.turno ? '#f59e0b' : c.turnoPropio ? '#0086ab' : '#e5e7eb'}`, background: editable ? 'white' : '#f9fafb' }}>
                            <option value="">Turno…</option>
                            {TURNOS.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                        )}
                        {c && ETIQUETA_ESTADO[c.estado] && <div style={{ fontSize: '10px', fontWeight: 700, color: COLOR_ESTADO[c.estado] }}>{ETIQUETA_ESTADO[c.estado]}</div>}
                        {c && c.mensaje && c.estado !== 'guardado' && c.estado !== 'ya_existia' && <div style={{ fontSize: '10px', color: COLOR_ESTADO[c.estado], maxWidth: '140px', margin: '2px auto 0' }}>{c.mensaje}</div>}
                        {c && c.estado === 'revision' && <button onClick={() => quitarCelda(filaIdx, fecha)} style={{ ...S.btn, padding: '2px 6px', fontSize: '10px', background: 'none', color: '#6b7280', textDecoration: 'underline' }}>Quitar de la captura</button>}
                        {yaTiene && <div style={{ fontSize: '10px', color: '#b45309', marginTop: '2px' }}>Ya tiene: {yaTiene.nombre_platillo}{yaTiene.turno ? ' · ' + yaTiene.turno : ' · sin turno'}</div>}
                      </td>
                    );
                  })}
                  <td style={S.td}>{filaRetirable(fila) && <button onClick={() => quitarFila(filaIdx)} title="Quitar empleado" style={{ ...S.btn, padding: '4px 8px', background: 'none', color: '#9ca3af' }}>✕</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {ocupado && <div style={{ marginBottom: '12px', color: '#6b7280', fontSize: '13px' }}>{ocupado}</div>}
      {aviso && <div role="alert" style={{ marginBottom: '12px', padding: '10px 14px', borderRadius: '8px', fontSize: '13px',
        background: aviso.tipo === 'error' ? '#fef2f2' : '#eff6ff', color: aviso.tipo === 'error' ? '#dc2626' : '#1d4ed8' }}>{aviso.tipo === 'error' ? '⚠️ ' : ''}{aviso.texto}</div>}

      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        <button onClick={guardar} disabled={!!ocupado || nEnviables === 0}
          style={{ ...S.btn, background: '#1d3d6b', color: 'white', opacity: (ocupado || nEnviables === 0) ? 0.6 : 1 }}>
          {hayIncierto ? `Reintentar (${nEnviables})` : `Guardar (${nEnviables} pedido${nEnviables !== 1 ? 's' : ''})`}
        </button>
        {hayCeldas && <button onClick={reconciliar} disabled={!!ocupado} style={{ ...S.btn, background: '#f3f4f6', color: '#374151' }}>Verificar qué quedó guardado</button>}
      </div>
    </div>
  );
}

// ── C.4b: modificar un pedido (cambiar platillo; C.5a-2: corregir turno) ──────
// Cambiar platillo usa dos IDs estables (el pedido anterior y el nuevo). CAMBIO_INCOMPLETO o sin respuesta → «Reintentar»
// con los mismos IDs; REVISION_REQUERIDA / FILA_INCOMPLETA / ID ajeno → sólo revisión, sin reintento. El pedido nuevo
// lleva el turno elegido. Corregir SÓLO el turno no crea pedido: va por la ruta protegida (sesión de RH) con el turno
// que se vio como versión.
export function ModalCambiarPedido({ usuario, pedido, cambioInicial, onCerrar, onHecho }) {
  const email = usuario?.email || '';
  const fecha = String(pedido.fecha_entrega).slice(0, 10);
  const turnoGuardado = pedido.turno || '';
  const [cambio, setCambio] = useState(cambioInicial || null);
  const [turnoSel, setTurnoSel] = useState((cambioInicial && cambioInicial.turno) || turnoGuardado);
  const [menuDia, setMenuDia] = useState([]);
  const [ensalada, setEnsalada] = useState(null);
  const [ocupado, setOcupado] = useState(cambioInicial ? 'Verificando…' : '');
  const [aviso, setAviso] = useState(null);

  const guardarCambio = c => { setCambio(c); actualizarBorrador(email, { cambio: c && c.estado !== 'hecho' ? { ...c, pedido } : null }); };

  useEffect(() => {
    const lunes = toYMD(getLunes(new Date(fecha + 'T12:00:00')));
    apiComedor('api/comedor/menu/semana', null, { semana_inicio: lunes }).then(d => {
      if (d.success) { setMenuDia(normalizarMenuDia((d.menu || {})[fecha])); setEnsalada(d.ensalada || null); }
    }).catch(() => {});
    if (cambioInicial) verificar(); // al restaurar: primero se consulta qué quedó guardado
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const verificar = async () => {
    setOcupado('Verificando…');
    try {
      const lunes = toYMD(getLunes(new Date(fecha + 'T12:00:00')));
      const d = await apiComedor('api/comedor/pedidos-semana', null, { semana_inicio: lunes });
      if (!d || !d.success) throw new Error((d && d.error) || 'sin respuesta');
      const c = reconciliarCambio(cambio || cambioInicial, d.pedidos || []);
      guardarCambio(c);
      if (c.estado === 'hecho') { setOcupado(''); onHecho('Cambio confirmado.'); return; }
      setAviso({ tipo: 'info', texto: c.mensaje });
    } catch (e) { setAviso({ tipo: 'error', texto: 'No se pudo verificar: ' + e.message }); }
    setOcupado('');
  };

  const opciones = [...menuDia.map(m => ({ id_platillo: String(m.id_platillo), nombre: m.nombre_platillo })), ...(ensalada ? [{ id_platillo: String(ensalada.id_platillo), nombre: ensalada.nombre }] : [])]
    .filter((o, i, a) => a.findIndex(x => x.id_platillo === o.id_platillo) === i && o.id_platillo !== String(pedido.id_platillo));
  const enCurso = cambio && ['enviando', 'incierto', 'reintentable'].includes(cambio.estado);

  const elegir = o => {
    if (enCurso && cambio.id_platillo !== o.id_platillo) { setAviso({ tipo: 'error', texto: 'Hay un cambio sin terminar: reintenta o verifica antes de elegir otro platillo.' }); return; }
    if (cambio && cambio.id_platillo === o.id_platillo) return;
    setAviso(null);
    guardarCambio({ id_pedido_anterior: pedido.id_pedido, id_pedido_nuevo: nuevoIdPedido(), id_platillo: o.id_platillo, nombre_platillo: o.nombre, turno: turnoSel, estado: 'listo' });
  };

  const elegirTurno = t => {
    if (enCurso) { setAviso({ tipo: 'error', texto: 'Hay un cambio sin terminar: reintenta o verifica antes de cambiar el turno.' }); return; }
    setAviso(null); setTurnoSel(t);
    if (cambio) guardarCambio({ ...cambio, turno: t });
  };

  const guardarTurno = async () => {
    setOcupado('Guardando turno…'); setAviso(null);
    let r = null;
    try { r = await escribirPadron(usuario, 'api/comedor/pedido/turno', { id_pedido: pedido.id_pedido, turno_esperado: turnoGuardado, turno: turnoSel }); } catch (e) { r = null; }
    setOcupado('');
    if (r && r.success) { onHecho(r.sin_cambios ? 'El turno ya era ' + r.turno + '.' : `Turno actualizado: ${r.turno}.` + (r.aviso === 'BITACORA_PENDIENTE' ? ' (la bitácora quedó pendiente)' : '')); return; }
    setAviso({ tipo: 'error', texto: !r ? 'Sin respuesta del servidor: vuelve a cargar y revisa el turno antes de reintentar.'
      : r.codigo === 'NO_AUTORIZADO' ? MENSAJE_NO_AUTORIZADO
      : r.codigo === 'CONFLICTO_VERSION' ? `El turno cambió mientras lo revisabas (ahora: ${r.turno_actual || 'sin turno'}). Cierra y vuelve a abrir el pedido.` : (r.error || 'No se pudo guardar el turno.') });
  };

  const enviar = async () => {
    if (!TURNOS.includes(cambio.turno)) { setAviso({ tipo: 'error', texto: 'Elige el turno del pedido antes de confirmar el cambio.' }); return; }
    const c = { ...cambio, estado: 'enviando' };
    guardarCambio(c); setOcupado('Cambiando…'); setAviso(null);
    let data = null;
    try { data = await apiComedor('api/comedor/pedido/cambiar', solicitudCambio(c)); } catch (e) { data = null; }
    const r = aplicarRespuestaCambio(c, data);
    guardarCambio(r); setOcupado('');
    if (r.estado === 'hecho') { onHecho(r.mensaje || 'Pedido cambiado.'); return; }
    setAviso({ tipo: 'error', texto: r.mensaje || 'No se pudo cambiar.' });
  };
  const descartar = () => { actualizarBorrador(email, { cambio: null }); onCerrar(); };

  return (
    <div style={S.overlay}>
      <div style={{ ...S.modal, width: '440px' }} role="dialog" aria-label="Modificar pedido">
        <h2 style={{ fontSize: '16px', fontWeight: 700, color: '#1d3d6b', marginBottom: '6px' }}>Modificar pedido</h2>
        <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 10px' }}><strong>{pedido.nombre_empleado}</strong> · {fmtFechaCorta(fecha)} · ahora: {pedido.nombre_platillo}</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px', fontSize: '13px' }}>
          <span>Turno guardado: <strong data-testid="turno-guardado" style={{ color: turnoGuardado ? '#1f2937' : '#b45309' }}>{turnoGuardado || 'Sin turno'}</strong></span>
          <select value={turnoSel} onChange={e => elegirTurno(e.target.value)} disabled={!!ocupado} aria-label="Turno del pedido"
            style={{ fontSize: '13px', padding: '3px 6px', borderRadius: '6px', border: `1px solid ${turnoSel ? '#d1d5db' : '#f59e0b'}` }}>
            <option value="">Turno…</option>
            {TURNOS.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div style={{ fontSize: '11px', color: '#9ca3af', textTransform: 'uppercase', marginBottom: '6px' }}>Cambiar platillo</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '14px' }}>
          {opciones.length === 0 && <div style={{ fontSize: '12px', color: '#9ca3af' }}>No hay otras opciones en el menú de ese día.</div>}
          {opciones.map(o => {
            const sel = cambio && cambio.id_platillo === o.id_platillo;
            return <button key={o.id_platillo} onClick={() => elegir(o)} disabled={!!ocupado}
              style={{ ...S.btn, textAlign: 'left', background: sel ? '#0086ab' : '#f3f4f6', color: sel ? 'white' : '#374151' }}>{o.nombre}</button>;
          })}
        </div>
        {ocupado && <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}>{ocupado}</div>}
        {aviso && <div role="alert" style={{ marginBottom: '10px', padding: '8px 12px', borderRadius: '6px', fontSize: '13px', background: aviso.tipo === 'error' ? '#fef2f2' : '#eff6ff', color: aviso.tipo === 'error' ? '#dc2626' : '#1d4ed8' }}>{aviso.texto}</div>}
        {cambio && cambio.estado === 'revision' && <div style={{ fontSize: '12px', color: '#b91c1c', marginBottom: '10px' }}>Este cambio requiere revisión en la hoja: no se reintentará.</div>}
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button onClick={onCerrar} disabled={!!ocupado} style={{ ...S.btn, background: '#f3f4f6', color: '#374151' }}>Cerrar</button>
          {!cambio && TURNOS.includes(turnoSel) && turnoSel !== turnoGuardado && <button onClick={guardarTurno} disabled={!!ocupado} style={{ ...S.btn, background: '#0086ab', color: 'white' }}>Guardar turno</button>}
          {cambio && !enCurso && cambio.estado !== 'hecho' && <button onClick={descartar} disabled={!!ocupado} style={{ ...S.btn, background: '#fee2e2', color: '#b91c1c' }}>Descartar</button>}
          {cambio && enCurso && <button onClick={verificar} disabled={!!ocupado} style={{ ...S.btn, background: '#f3f4f6', color: '#374151' }}>Verificar</button>}
          {cambio && (cambio.estado === 'listo' || cambio.estado === 'error') && <button onClick={enviar} disabled={!!ocupado} style={{ ...S.btn, background: '#0086ab', color: 'white' }}>Confirmar cambio</button>}
          {cambio && enCurso && cambio.estado !== 'enviando' && <button onClick={enviar} disabled={!!ocupado} style={{ ...S.btn, background: '#d97706', color: 'white' }}>Reintentar</button>}
        </div>
      </div>
    </div>
  );
}

// ── Tab 2: Pedidos ────────────────────────────────────────────────────────────

function TabPedidos({ usuario }) {
  const email = usuario?.email || '';
  const hoyDate = new Date();
  const diaHoy = hoyDate.getDay();
  const [semana, setSemana] = useState(() => toYMD(getLunes()));
  const [fechaVista, setFechaVista] = useState(() => {
    if (diaHoy >= 1 && diaHoy <= 5) return toYMD(hoyDate);
    return toYMD(getLunes());
  });
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalCocina, setModalCocina] = useState(false);
  const [captura, setCaptura] = useState(null);          // { modo, borrador }
  const [cambio, setCambio] = useState(null);            // { pedido, cambioInicial }
  const [generandoDiario, setGenerandoDiario] = useState(false);
  const [generandoSemanal, setGenerandoSemanal] = useState(false);
  const [generandoEtiquetas, setGenerandoEtiquetas] = useState(false);
  const [revisionEtiquetas, setRevisionEtiquetas] = useState(null);   // pedidos sin turno o sin destino
  const [modalConfirmarCancelar, setModalConfirmarCancelar] = useState(false);
  const [pedidoACancelar, setPedidoACancelar] = useState(null);
  const [modalElegirNave, setModalElegirNave] = useState(false);
  const [verCancelados, setVerCancelados] = useState(false);
  const [mensaje, setMensaje] = useState(null);
  const [borrador, setBorrador] = useState(() => leerBorrador(email));

  const diasSem = diasSemana(semana);

  const cargarPedidos = useCallback(async () => {
    setLoading(true);
    const data = await apiComedor('api/comedor/pedidos-semana', null, { semana_inicio: semana });
    if (data.success) setPedidos(data.pedidos || []);
    setLoading(false);
  }, [semana]);

  useEffect(() => { cargarPedidos(); }, [cargarPedidos]);

  const pedidosDia = pedidos.filter(p => String(p.fecha_entrega).slice(0, 10) === fechaVista);
  const pedidosActivos = pedidosDia.filter(p => p.estado === 'activo');
  const pedidosCancelados = pedidosDia.filter(p => p.estado !== 'activo');
  const totalActivos = pedidosActivos.length;
  const porciones = pedidosActivos.reduce((acc, p) => {
    const k = p.nombre_platillo || '?';
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});

  const handleGenerarListadoDiario = async () => {
    if (!totalActivos) return;
    setGenerandoDiario(true);
    const data = await apiComedor('api/comedor/listado-diario-plantilla', null, { fecha: fechaVista });
    setGenerandoDiario(false);
    if (data.success) descargarPDF(data.base64, data.nombre || `Listado_Diario_${fechaVista}.pdf`);
    else alert(data.error || 'Error al generar listado diario');
  };

  // C.5a-2 — Etiquetas: se revisan SÓLO los pedidos que se imprimen (activos del día, sin SOPORTE); si a alguno le falta
  // el turno o el destino, no se generan y se listan para corregirlos. El backend vuelve a revisar con los datos guardados.
  const pedidosEtiqueta = pedidosActivos.filter(p => String(p.nombre_empleado || '').trim() !== 'SOPORTE');
  const pendientesEtiqueta = lista => lista.filter(p => !TURNOS.includes(String(p.turno || '')) || !AREAS_COMEDOR.includes(String(p.ubicacion_comedor || '').trim()))
    .map(p => ({ id_pedido: p.id_pedido, id_empleado: p.id_empleado, nombre: p.nombre_empleado,
      falta: [!TURNOS.includes(String(p.turno || '')) ? 'turno' : null, !AREAS_COMEDOR.includes(String(p.ubicacion_comedor || '').trim()) ? 'destino' : null].filter(Boolean) }));
  const handleEtiquetas = async () => {
    setMensaje(null);
    const locales = pendientesEtiqueta(pedidosEtiqueta);
    if (locales.length) { setRevisionEtiquetas(locales); return; }
    setGenerandoEtiquetas(true);
    let data = null;
    try { data = await apiComedor('api/comedor/etiquetas-plantilla', null, { fecha: fechaVista }); } catch (e) { data = null; }
    setGenerandoEtiquetas(false);
    if (data && data.success) {
      descargarPDF(data.base64, data.nombre || `Etiquetas_${fechaVista}.pdf`);
      setMensaje({ tipo: 'ok', texto: `Etiquetas: ${data.total} (${Object.entries(data.por_destino || {}).map(([k, v]) => k + ' ' + v).join(' · ')}; ${Object.entries(data.por_turno || {}).map(([k, v]) => k + ' ' + v).join(' · ')}).` });
    } else if (data && data.codigo === 'REVISION_REQUERIDA') setRevisionEtiquetas(data.pendientes || []);
    else setMensaje({ tipo: 'error', texto: (data && data.error) || 'No se pudieron generar las etiquetas.' });
  };

  const handleGenerarListadoSemanal = async (nave) => {
    if (!nave) { setModalElegirNave(false); return; }
    setGenerandoSemanal(true);
    const data = await apiComedor('api/comedor/listado-semanal-plantilla', null, { semana_inicio: semana, nave });
    setGenerandoSemanal(false);
    setModalElegirNave(false);
    if (data.success) descargarPDF(data.base64, data.nombre || `Listado_Semanal_${nave}.pdf`);
    else alert(data.error || 'Error al generar listado semanal');
  };

  const moverSemana = dir => {
    const d = new Date(semana + 'T12:00:00');
    d.setDate(d.getDate() + dir * 7);
    const nuevaLunes = toYMD(getLunes(d));
    setSemana(nuevaLunes);
    setFechaVista(diasSemana(nuevaLunes)[0]);
  };

  const cerrarCaptura = texto => { setCaptura(null); setBorrador(leerBorrador(email)); if (texto) setMensaje({ tipo: 'ok', texto }); cargarPedidos(); };
  const cerrarCambio = texto => { setCambio(null); setBorrador(leerBorrador(email)); if (texto) setMensaje({ tipo: 'ok', texto }); cargarPedidos(); };

  const cancelar = async () => {
    setModalConfirmarCancelar(false);
    let data = null;
    try { data = await apiComedor('api/comedor/pedido/cancelar', { id_pedido: pedidoACancelar.id_pedido, cliente: CLIENTE_C4B }); } catch (e) { data = null; }
    if (data && data.success) {
      setMensaje({ tipo: 'ok', texto: data.ya_cancelado ? 'El pedido ya estaba cancelado.' : data.aviso === 'BITACORA_PENDIENTE' ? 'Pedido cancelado (la bitácora quedó pendiente).' : 'Pedido cancelado.' });
      cargarPedidos();
    } else {
      setMensaje({ tipo: 'error', texto: data ? (data.error || 'Error al cancelar') : 'Sin respuesta del servidor: revisa la lista antes de volver a cancelar.' });
      cargarPedidos();
    }
    setPedidoACancelar(null);
  };

  if (captura) {
    return <CapturaPedidos usuario={usuario} modo={captura.modo} semanaInicial={semana} borrador={captura.borrador}
      onCerrar={() => cerrarCaptura(null)} onTerminado={texto => cerrarCaptura(texto)} />;
  }

  const capturaPendiente = borrador && borrador.captura && (borrador.captura.filas || []).flatMap(f => Object.values(f.celdas || {})).filter(c => !FINALES.includes(c.estado)).length;
  const cambioPendiente = borrador && borrador.cambio;

  return (
    <div style={{ padding: '24px' }}>
      {/* Borrador de sesión sin terminar */}
      {(capturaPendiente > 0 || cambioPendiente) && (
        <div role="status" style={{ marginBottom: '16px', padding: '10px 14px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '8px', fontSize: '13px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          {capturaPendiente > 0 && <>
            <span>Tienes una captura sin terminar ({capturaPendiente} pedido{capturaPendiente !== 1 ? 's' : ''}).</span>
            <button onClick={() => setCaptura({ modo: borrador.captura.modo, borrador: borrador.captura })} style={{ ...S.btn, padding: '4px 10px', background: '#f59e0b', color: 'white' }}>Revisar captura</button>
          </>}
          {cambioPendiente && <>
            <span>Hay un cambio de platillo sin terminar ({cambioPendiente.pedido?.nombre_empleado}).</span>
            <button onClick={() => setCambio({ pedido: cambioPendiente.pedido, cambioInicial: cambioPendiente })} style={{ ...S.btn, padding: '4px 10px', background: '#f59e0b', color: 'white' }}>Revisar cambio</button>
          </>}
        </div>
      )}
      {mensaje && <div role="alert" style={{ marginBottom: '16px', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', background: mensaje.tipo === 'ok' ? '#f0fdf4' : '#fef2f2', color: mensaje.tipo === 'ok' ? '#166534' : '#dc2626' }}>{mensaje.texto}</div>}

      {/* Header — selector de semana */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button onClick={() => moverSemana(-1)} aria-label="Semana anterior" style={{ ...S.btn, background: '#f3f4f6', color: '#374151', padding: '6px 12px' }}>‹</button>
          <span style={{ fontWeight: 600, fontSize: '15px', color: '#1d3d6b', minWidth: '170px', textAlign: 'center' }}>
            Semana {semanaLabel(semana)}
          </span>
          <button onClick={() => moverSemana(1)} aria-label="Semana siguiente" style={{ ...S.btn, background: '#f3f4f6', color: '#374151', padding: '6px 12px' }}>›</button>
          <button onClick={() => { const l = toYMD(getLunes()); setSemana(l); setFechaVista(diasSemana(l)[0]); }}
            style={{ ...S.btn, background: '#f3f4f6', color: '#374151', fontSize: '12px', padding: '6px 10px' }}>Hoy</button>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={() => { setMensaje(null); setCaptura({ modo: 'individual', borrador: null }); }} disabled={capturaPendiente > 0} title={capturaPendiente > 0 ? 'Termina o descarta la captura pendiente' : ''}
            style={{ ...S.btn, background: '#f3f4f6', color: '#374151', display: 'flex', alignItems: 'center', gap: '6px', opacity: capturaPendiente > 0 ? 0.5 : 1 }}>
            <i className="ti ti-plus"></i> Registrar
          </button>
          <button onClick={() => { setMensaje(null); setCaptura({ modo: 'semanal', borrador: null }); }} disabled={capturaPendiente > 0} title={capturaPendiente > 0 ? 'Termina o descarta la captura pendiente' : ''}
            style={{ ...S.btn, background: '#1d3d6b', color: 'white', display: 'flex', alignItems: 'center', gap: '6px', opacity: capturaPendiente > 0 ? 0.5 : 1 }}>
            <i className="ti ti-table"></i> Carga semanal
          </button>
        </div>
      </div>

      {/* Day tabs: cuentan sólo los pedidos activos */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '20px' }}>
        {diasSem.map((fecha, i) => {
          const cntActivos = pedidos.filter(p => String(p.fecha_entrega).slice(0, 10) === fecha && p.estado === 'activo').length;
          const esHoy = fecha === toYMD(new Date());
          const sel = fechaVista === fecha;
          return (
            <button key={fecha} onClick={() => setFechaVista(fecha)}
              style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '10px 4px',
                border: 'none', cursor: 'pointer', borderRadius: '10px',
                background: sel ? '#0086ab' : esHoy ? '#e0f2fe' : '#f3f4f6',
                color: sel ? 'white' : esHoy ? '#0369a1' : '#374151', fontWeight: sel ? 600 : 400 }}>
              <span style={{ fontSize: '12px', fontWeight: 600 }}>{DIAS_NOMBRES[i]}</span>
              <span style={{ fontSize: '11px', opacity: 0.8, marginTop: '2px' }}>{fmtFechaCorta(fecha)}</span>
              {cntActivos > 0 && <span style={{ fontSize: '11px', marginTop: '3px', fontWeight: 700 }}>{cntActivos}</span>}
            </button>
          );
        })}
      </div>

      {/* KPI bar: sólo activos */}
      <div style={{ ...S.card, marginBottom: '16px', padding: '14px 20px' }} aria-label="Conteo del día">
        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total pedidos</div>
            <div style={{ fontSize: '28px', fontWeight: 700, color: '#1d3d6b', lineHeight: 1 }} data-total-activos>{totalActivos}</div>
          </div>
          {Object.keys(porciones).length > 0 && <div style={{ width: '1px', height: '40px', background: '#e5e7eb' }} />}
          {Object.entries(porciones).map(([nombre, cnt]) => (
            <div key={nombre}>
              <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '2px' }}>{nombre}</div>
              <div style={{ fontSize: '22px', fontWeight: 700, color: '#0086ab', lineHeight: 1 }}>{cnt}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <button onClick={handleGenerarListadoDiario} disabled={!totalActivos || generandoDiario}
          style={{ ...S.btn, background: '#0086ab', color: 'white', display: 'flex', alignItems: 'center', gap: '6px', opacity: !totalActivos || generandoDiario ? 0.5 : 1 }}>
          <i className="ti ti-file-type-pdf"></i>
          {generandoDiario ? 'Generando...' : 'Listado Diario'}
        </button>
        <button onClick={handleEtiquetas} disabled={!pedidosEtiqueta.length || generandoEtiquetas}
          style={{ ...S.btn, background: '#0f766e', color: 'white', display: 'flex', alignItems: 'center', gap: '6px', opacity: !pedidosEtiqueta.length || generandoEtiquetas ? 0.5 : 1 }}>
          <i className="ti ti-tags"></i>
          {generandoEtiquetas ? 'Generando...' : 'Etiquetas'}
        </button>
        <button onClick={() => setModalElegirNave(true)} disabled={generandoSemanal}
          style={{ ...S.btn, background: '#8b5cf6', color: 'white', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <i className="ti ti-printer"></i>
          {generandoSemanal ? 'Generando...' : 'Imprimir Semanal'}
        </button>
        <button onClick={() => setModalCocina(true)} disabled={!totalActivos}
          style={{ ...S.btn, background: '#f3f4f6', color: '#374151', display: 'flex', alignItems: 'center', gap: '6px', opacity: !totalActivos ? 0.5 : 1 }}>
          <i className="ti ti-brand-whatsapp"></i> Avisar a Cocina
        </button>
      </div>

      {/* Pedidos activos del día */}
      <div style={{ ...S.card, padding: 0, overflow: 'hidden' }} aria-label="Pedidos activos">
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>Cargando pedidos...</div>
        ) : pedidosActivos.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
            <i className="ti ti-bowl" style={{ fontSize: '32px', display: 'block', marginBottom: '8px' }}></i>
            Sin pedidos activos para este día
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#f9fafb' }}>
              <tr>
                <th style={S.th}>Empleado</th>
                <th style={S.th}>Destino de entrega</th>
                <th style={S.th}>Turno</th>
                <th style={S.th}>Sucursal laboral</th>
                <th style={S.th}>Platillo</th>
                <th style={S.th}>Capturado por</th>
                <th style={S.th}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {pedidosActivos.map(p => (
                <tr key={p.id_pedido} data-pedido={p.id_pedido}>
                  <td style={S.td}>{p.nombre_empleado}</td>
                  <td style={S.td}>{p.ubicacion_comedor || <span style={{ color: '#9ca3af' }}>—</span>}</td>
                  <td style={S.td} data-turno>{p.turno || <span style={{ fontSize: '11px', fontWeight: 600, color: '#b45309', background: '#fffbeb', padding: '1px 6px', borderRadius: '99px' }}>Sin turno</span>}</td>
                  <td style={S.td}>{expandirSucursal(p.sucursal) || <span style={{ color: '#9ca3af' }}>—</span>}</td>
                  <td style={S.td}>{p.nombre_platillo}</td>
                  <td style={S.td}><span style={{ fontSize: '11px', color: '#9ca3af' }}>{p.pedido_por}</span></td>
                  <td style={{ ...S.td, display: 'flex', gap: '6px' }}>
                    <button onClick={() => { setMensaje(null); setCambio({ pedido: p, cambioInicial: null }); }} disabled={!!cambioPendiente} title={cambioPendiente ? 'Termina el cambio pendiente' : 'Cambiar platillo o corregir turno'} style={S.btnEdit}>
                      <i className="ti ti-arrows-exchange" style={{ fontSize: '12px' }}></i>Modificar
                    </button>
                    <button onClick={() => { setPedidoACancelar(p); setModalConfirmarCancelar(true); }} style={S.btnCancel} title="Cancelar este pedido">
                      <i className="ti ti-x" style={{ fontSize: '12px' }}></i>Cancelar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Cancelados: aparte y fuera del conteo */}
      {pedidosCancelados.length > 0 && (
        <div style={{ marginTop: '12px' }} aria-label="Pedidos cancelados">
          <button onClick={() => setVerCancelados(v => !v)} style={{ ...S.btn, background: 'none', color: '#6b7280', padding: '4px 0', fontSize: '12px' }}>
            {verCancelados ? '▾' : '▸'} Cancelados ({pedidosCancelados.length}) · no cuentan en el total
          </button>
          {verCancelados && (
            <div style={{ ...S.card, padding: 0, overflow: 'hidden', opacity: 0.75 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {pedidosCancelados.map(p => (
                    <tr key={p.id_pedido} data-pedido={p.id_pedido}>
                      <td style={{ ...S.td, textDecoration: 'line-through' }}>{p.nombre_empleado}</td>
                      <td style={{ ...S.td, textDecoration: 'line-through' }}>{p.nombre_platillo}</td>
                      <td style={S.td}><span style={{ fontSize: '11px', color: '#9ca3af' }}>{p.estado}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Modales */}
      {modalCocina && <ModalAvisarCocina fecha={fechaVista} onClose={() => setModalCocina(false)} />}
      {revisionEtiquetas && (
        <div style={S.overlay}>
          <div style={{ ...S.modal, width: '480px' }} role="dialog" aria-label="Revisar antes de imprimir etiquetas">
            <h2 style={{ fontSize: '16px', fontWeight: 700, color: '#1d3d6b', marginBottom: '6px' }}>Revisar antes de imprimir</h2>
            <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 12px' }}>Estos pedidos no se pueden imprimir así (sin turno, sin destino de entrega o con un texto que no cabe en la etiqueta). Revísalos y vuelve a generar las etiquetas.</p>
            <ul data-testid="pendientes-etiquetas" style={{ listStyle: 'none', padding: 0, margin: '0 0 14px', maxHeight: '260px', overflowY: 'auto' }}>
              {revisionEtiquetas.map(x => {
                const p = pedidosActivos.find(y => y.id_pedido === x.id_pedido);
                return (
                  <li key={x.id_pedido} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', padding: '6px 0', borderBottom: '1px solid #f3f4f6', fontSize: '13px' }}>
                    <span>#{x.id_empleado} {x.nombre}
                      {(x.falta || []).length > 0 && <> · <span style={{ color: '#b45309' }}>falta {x.falta.join(' y ')}</span></>}
                      {(x.largos || []).length > 0 && <> · <span data-testid="texto-largo" style={{ color: '#b45309' }}>no cabe en la etiqueta: {x.largos.join(' y ')}</span></>}
                    </span>
                    {p && (x.falta || []).includes('turno') && <button onClick={() => { setRevisionEtiquetas(null); setCambio({ pedido: p, cambioInicial: null }); }} style={S.btnEdit}>Corregir turno</button>}
                    {(x.falta || []).includes('destino') &&<span data-testid="ayuda-destino" style={{ fontSize: '11px', color: '#6b7280', maxWidth: '200px' }}>Sin destino guardado: cancela este pedido y vuelve a registrarlo con su destino.</span>}
                  </li>
                );
              })}
            </ul>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setRevisionEtiquetas(null)} style={{ ...S.btn, background: '#f3f4f6', color: '#374151' }}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
      {cambio && <ModalCambiarPedido usuario={usuario} pedido={cambio.pedido} cambioInicial={cambio.cambioInicial}
        onCerrar={() => cerrarCambio(null)} onHecho={texto => cerrarCambio(texto)} />}
      {modalConfirmarCancelar && pedidoACancelar && (
        <div style={S.overlay}>
          <div style={{ ...S.modal, width: '380px' }}>
            <div style={{ marginBottom: '20px' }}>
              <h2 style={{ fontSize: '16px', fontWeight: 700, color: '#1d3d6b', marginBottom: '8px' }}>Cancelar Pedido</h2>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                ¿Cancelar pedido de <strong>{pedidoACancelar.nombre_empleado}</strong> ({pedidoACancelar.nombre_platillo})?
              </p>
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => { setModalConfirmarCancelar(false); setPedidoACancelar(null); }}
                style={{ ...S.btn, background: '#f3f4f6', color: '#374151' }}>
                No, mantener
              </button>
              <button onClick={cancelar} style={{ ...S.btn, background: '#dc2626', color: 'white' }}>
                Sí, cancelar
              </button>
            </div>
          </div>
        </div>
      )}
      {modalElegirNave && (
        <div style={S.overlay}>
          <div style={{ ...S.modal, width: '380px' }}>
            <div style={{ marginBottom: '20px' }}>
              <h2 style={{ fontSize: '16px', fontWeight: 700, color: '#1d3d6b', marginBottom: '8px' }}>Elegir Nave</h2>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                ¿Cuál nave deseas imprimir?
              </p>
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setModalElegirNave(false)}
                style={{ ...S.btn, background: '#f3f4f6', color: '#374151' }}>
                Cancelar
              </button>
              <button onClick={() => handleGenerarListadoSemanal('NAVE 1')}
                style={{ ...S.btn, background: '#3b82f6', color: 'white' }}>
                NAVE 1
              </button>
              <button onClick={() => handleGenerarListadoSemanal('NAVE 2')}
                style={{ ...S.btn, background: '#10b981', color: 'white' }}>
                NAVE 2
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
// Selector de una opción del menú: buscar escribiendo, elegir, limpiar o proponer el alta de un platillo.
export function SelectorPlatillo({ valor, inactivo, platillos, todos, recientes, onElegir, onLimpiar, onAgregar, etiqueta }) {
  const [abierto, setAbierto] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [pos, setPos] = useState(null);
  const ref = React.useRef(null);
  const cerrar = useCallback(() => { setAbierto(false); setBusqueda(''); }, []);

  useEffect(() => {
    if (!abierto) return undefined;
    const fuera = e => { if (ref.current && !ref.current.contains(e.target)) cerrar(); };
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, [abierto, cerrar]);

  // La lista flota sobre la página (position: fixed) para que el desplazamiento horizontal de la
  // tabla no la recorte. Se ubica bajo la celda (o encima si abajo no cabe), dentro de la ventana,
  // y se reubica al desplazar o redimensionar.
  const ubicar = useCallback(() => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const margen = 8, vw = window.innerWidth, vh = window.innerHeight;
    const ancho = Math.min(Math.max(r.width, 240), vw - 2 * margen);
    const left = Math.min(Math.max(r.left, margen), vw - ancho - margen);
    const abajo = vh - r.bottom - margen, arriba = r.top - margen;
    const haciaArriba = abajo < 220 && arriba > abajo;
    const alto = Math.max((haciaArriba ? arriba : abajo) - 4, 120);
    setPos(haciaArriba ? { left, width: ancho, bottom: vh - r.top + 4, alto } : { left, width: ancho, top: r.bottom + 4, alto });
  }, []);
  useLayoutEffect(() => {
    if (!abierto) return undefined;
    ubicar();
    window.addEventListener('scroll', ubicar, true);
    window.addEventListener('resize', ubicar);
    return () => { window.removeEventListener('scroll', ubicar, true); window.removeEventListener('resize', ubicar); };
  }, [abierto, ubicar]);

  const s = abierto ? seccionesSelector(platillos, recientes, busqueda, todos) : null;
  const elegir = p => { onElegir(p); cerrar(); };
  const primera = s && (s.coincidencias ? s.coincidencias[0] : (s.recientes[0] || s.resto[0]));
  const tecla = e => {
    if (e.key === 'Escape') cerrar();
    if (e.key === 'Enter' && primera) elegir(primera);
  };
  const fila = p => (
    <div key={p.id_platillo} role="option" aria-selected={false} onClick={() => elegir(p)}
      style={{ padding: '7px 10px', fontSize: '12px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}
      onMouseEnter={e => { e.currentTarget.style.background = '#f0f9ff'; }}
      onMouseLeave={e => { e.currentTarget.style.background = ''; }}>
      {p.nombre}
    </div>
  );
  const titulo = texto => <div style={{ padding: '6px 10px 4px', fontSize: '10px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{texto}</div>;
  const aviso = texto => <div style={{ padding: '8px 10px', fontSize: '12px', color: '#92400e', background: '#fffbeb' }}>{texto}</div>;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'stretch', border: '1px solid #e5e7eb', borderRadius: '6px', background: 'white' }}>
        <button type="button" aria-label={etiqueta} onClick={() => { if (abierto) { cerrar(); } else { ubicar(); setAbierto(true); } }}
          style={{ flex: 1, minWidth: 0, textAlign: 'left', padding: '6px 8px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '12px', color: valor ? '#1f2937' : '#9ca3af' }}>
          {valor ? valor.nombre : '— sin opción —'}
          {valor && inactivo && <span style={{ marginLeft: '6px', color: '#b45309', fontSize: '11px' }}>(inactivo)</span>}
        </button>
        {valor && (
          <button type="button" aria-label={`Quitar ${etiqueta}`} title="Quitar esta opción" onClick={() => { onLimpiar(); cerrar(); }}
            style={{ border: 'none', borderLeft: '1px solid #e5e7eb', background: 'none', cursor: 'pointer', color: '#9ca3af', padding: '0 8px', fontSize: '12px' }}>✕</button>
        )}
      </div>
      {abierto && (
        <div style={{ position: 'fixed', zIndex: 1000, left: pos ? pos.left : 0, width: pos ? pos.width : 240,
          ...(pos && pos.bottom !== undefined ? { bottom: pos.bottom } : { top: pos ? pos.top : 0 }),
          background: 'white',
          border: '1px solid #e5e7eb', borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}>
          <input autoFocus aria-label={`Buscar platillo para ${etiqueta}`} value={busqueda} onChange={e => setBusqueda(e.target.value)} onKeyDown={tecla}
            placeholder="Escribe para buscar..." style={{ ...S.input, border: 'none', borderBottom: '1px solid #e5e7eb', borderRadius: '8px 8px 0 0', fontSize: '12px' }} />
          <div role="listbox" style={{ maxHeight: pos ? Math.min(260, Math.max(pos.alto - 40, 80)) + 'px' : '260px', overflowY: 'auto' }}>
            {s.coincidencias ? (
              <>
                {s.coincidencias.map(fila)}
                {s.inactivo && aviso('Este platillo existe pero está inactivo. Reactívalo desde Catálogo.')}
                {s.fijo && aviso('Este platillo ya aparece todos los días (siempre disponible).')}
                {s.coincidencias.length === 0 && !s.inactivo && !s.fijo && !s.agregar && <div style={{ padding: '8px 10px', fontSize: '12px', color: '#9ca3af' }}>Sin coincidencias</div>}
                {s.agregar && (
                  <div role="button" onClick={() => { onAgregar(s.agregar); cerrar(); }}
                    style={{ padding: '8px 10px', fontSize: '12px', cursor: 'pointer', color: '#0369a1', fontWeight: 600, background: '#f0f9ff' }}>
                    + Agregar "{s.agregar}"
                  </div>
                )}
              </>
            ) : (
              <>
                {s.recientes.length > 0 && titulo('Recientes')}
                {s.recientes.map(fila)}
                {s.resto.length > 0 && titulo('Todos los platillos')}
                {s.resto.map(fila)}
                {s.recientes.length + s.resto.length === 0 && <div style={{ padding: '8px 10px', fontSize: '12px', color: '#9ca3af' }}>No hay platillos activos en el catálogo</div>}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function TabMenu() {
  const [semana, setSemana] = useState(() => toYMD(getLunes()));
  const [catalogo, setCatalogo] = useState([]);          // catálogo completo (activos e inactivos)
  const [ensalada, setEnsalada] = useState(null);
  const [recientes, setRecientes] = useState([]);        // ids de platillos usados recientemente
  const [pedidos, setPedidos] = useState([]);            // pedidos de la semana (para avisar antes de retirar un platillo)
  const [base, setBase] = useState({});                  // menú tal como está guardado: { fecha: { 1: { id_platillo, nombre }, 2, 3 } }
  const [menu, setMenu] = useState({});                  // menú en edición, misma forma
  const [alta, setAlta] = useState(null);                // { fecha, n, nombre } mientras se confirma un platillo nuevo
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [msg, setMsg] = useState(null);

  const diasSem = diasSemana(semana);
  const seleccionables = catalogo.filter(p => p.activo && !p.siempre_disponible);
  const modificados = diasModificadosMenu(base, menu, diasSem);

  const cargar = useCallback(async () => {
    setLoading(true);
    const [menData, platData, recData, pedData] = await Promise.all([
      apiComedor('api/comedor/menu/semana', null, { semana_inicio: semana }),
      apiComedor('api/comedor/platillos'),
      apiComedor('api/comedor/menu/recientes'),
      apiComedor('api/comedor/pedidos-semana', null, { semana_inicio: semana }),
    ]);
    if (platData.success) setCatalogo(platData.platillos || []);
    setRecientes(recData.success ? (recData.platillos || []).map(p => p.id_platillo) : []);
    setPedidos(pedData.success ? (pedData.pedidos || []) : []);
    if (menData.success) {
      setEnsalada(menData.ensalada || null);
      const cargado = {};
      diasSemana(semana).forEach(fecha => {
        cargado[fecha] = {};
        normalizarMenuDia((menData.menu || {})[fecha]).forEach(m => { cargado[fecha][m.numero] = { id_platillo: m.id_platillo, nombre: m.nombre_platillo }; });
      });
      setBase(cargado);
      setMenu(cargado);
    }
    setLoading(false);
  }, [semana]);

  useEffect(() => { cargar(); }, [cargar]);

  const irASemana = lunes => {
    if (lunes === semana) return;
    if (modificados.length && !window.confirm('Hay cambios sin guardar en esta semana. Si cambias de semana se perderán.\n¿Deseas continuar?')) return;
    setMsg(null);
    setSemana(lunes);
  };
  const moverSemana = dir => {
    const d = new Date(semana + 'T12:00:00');
    d.setDate(d.getDate() + dir * 7);
    irASemana(toYMD(getLunes(d)));
  };

  const ponerOpcion = (fecha, n, platillo) => setMenu(prev => {
    const dia = { ...(prev[fecha] || {}) };
    if (platillo) dia[n] = { id_platillo: platillo.id_platillo, nombre: platillo.nombre }; else delete dia[n];
    return { ...prev, [fecha]: dia };
  });

  const handleGuardar = async () => {
    // Avisos previos, día por día. Si RH no confirma un día, ese día no se envía y sigue marcado como modificado.
    const aGuardar = [];
    for (const fecha of modificados) {
      const dia = etiquetaDiaMenu(fecha, diasSem.indexOf(fecha));
      const afectados = pedidosAfectadosMenu(base[fecha], menu[fecha], pedidos.filter(p => String(p.fecha_entrega).slice(0, 10) === fecha));
      const avisos = [];
      if (afectados.length) {
        const total = afectados.reduce((s, a) => s + a.cantidad, 0);
        avisos.push(`Hay ${total} pedido${total !== 1 ? 's' : ''} activo${total !== 1 ? 's' : ''} de ${enumerar(afectados.map(a => (afectados.length > 1 ? `${a.nombre} (${a.cantidad})` : a.nombre)))}.\nEl cambio del menú no modificará esos pedidos.`);
      }
      if (opcionesDiaMenu(menu[fecha]).length === 0) avisos.push('Este día quedará sin menú programado.');
      if (avisos.length && !window.confirm(`${dia.charAt(0).toUpperCase() + dia.slice(1)}:\n${avisos.join('\n')}\n¿Deseas continuar?`)) continue;
      aGuardar.push(fecha);
    }
    if (aGuardar.length === 0) return;

    setGuardando(true); setMsg(null);
    const guardados = {}, fallidos = [];
    for (const fecha of aGuardar) {
      let data;
      try { data = await apiComedor('api/comedor/menu/programar-dia', { fecha, platillos: opcionesDiaMenu(menu[fecha]) }); }
      catch { data = { success: false, error: 'Error de conexión' }; }
      if (data.success) guardados[fecha] = { ...(menu[fecha] || {}) };
      else fallidos.push({ dia: etiquetaDiaMenu(fecha, diasSem.indexOf(fecha)), error: data.error || 'Error al guardar' });
    }
    // Sólo los días guardados pasan a ser el nuevo punto de comparación; los fallidos siguen como modificados.
    setBase(prev => ({ ...prev, ...guardados }));
    setGuardando(false);
    if (fallidos.length) {
      setMsg({ tipo: 'err', texto: `No se pudo guardar: ${enumerar(fallidos.map(f => f.dia))}.`, detalle: fallidos.map(f => `${f.dia}: ${f.error}`) });
    } else {
      setMsg({ tipo: 'ok', texto: 'Menú guardado correctamente.' });
      setTimeout(() => setMsg(m => (m && m.tipo === 'ok' ? null : m)), 3000);
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      {/* Navegación */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#1d3d6b' }}>Menú Semana</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button onClick={() => moverSemana(-1)} style={{ ...S.btn, background: '#f3f4f6', color: '#374151', padding: '6px 14px' }}>‹ Anterior</button>
          <button onClick={() => irASemana(toYMD(getLunes()))} style={{ ...S.btn, background: '#e0f2fe', color: '#0369a1', fontSize: '12px', padding: '6px 12px' }}>Semana actual</button>
          <button onClick={() => moverSemana(1)} style={{ ...S.btn, background: '#f3f4f6', color: '#374151', padding: '6px 14px' }}>Siguiente ›</button>
        </div>
      </div>
      <p style={{ color: '#6b7280', fontSize: '13px', marginBottom: '20px' }}>
        <i className="ti ti-calendar-week" style={{ marginRight: '6px' }}></i>{semanaLabel(semana)}
      </p>

      {msg && (
        <div style={{ marginBottom: '16px', padding: '10px 14px', borderRadius: '8px', fontSize: '13px',
          background: msg.tipo === 'err' ? '#fef2f2' : '#f0fdf4',
          color: msg.tipo === 'err' ? '#dc2626' : '#166534',
          border: `1px solid ${msg.tipo === 'err' ? '#fecaca' : '#bbf7d0'}` }}>
          {msg.tipo === 'err' ? '⚠️' : '✅'} {msg.texto}
          {msg.detalle && msg.detalle.map(d => <div key={d} style={{ fontSize: '12px', marginTop: '4px' }}>{d}</div>)}
        </div>
      )}

      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>Cargando menú...</div>
      ) : (
        <>
          {/* Grid de platillos */}
          <div style={{ ...S.card, padding: 0, overflowX: 'auto', marginBottom: '20px' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead style={{ background: '#f9fafb' }}>
                <tr>
                  <th style={{ ...S.th, width: '60px' }}>Op.</th>
                  {diasSem.map((fecha, i) => (
                    <th key={fecha} style={{ ...S.th, textAlign: 'center', minWidth: '140px' }}>
                      {DIAS_NOMBRES[i]}<br />
                      <span style={{ fontSize: '11px', fontWeight: 400, color: '#9ca3af' }}>{fmtFechaCorta(fecha)}</span>
                      {modificados.includes(fecha) && <div style={{ fontSize: '10px', fontWeight: 600, color: '#b45309' }}>sin guardar</div>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[1, 2, 3].map(n => (
                  <tr key={n}>
                    <td style={{ ...S.td, fontWeight: 700, color: '#0086ab', textAlign: 'center' }}>{n}</td>
                    {diasSem.map((fecha, i) => {
                      const valor = (menu[fecha] || {})[n] || null;
                      const enCatalogo = valor && catalogo.find(p => p.id_platillo === valor.id_platillo);
                      return (
                        <td key={fecha} style={{ ...S.td, padding: '8px' }}>
                          <SelectorPlatillo
                            etiqueta={`opción ${n} del ${etiquetaDiaMenu(fecha, i)}`}
                            valor={valor}
                            inactivo={!!valor && !(enCatalogo && enCatalogo.activo)}
                            platillos={seleccionables} todos={catalogo} recientes={recientes}
                            onElegir={p => ponerOpcion(fecha, n, p)}
                            onLimpiar={() => ponerOpcion(fecha, n, null)}
                            onAgregar={nombre => setAlta({ fecha, n, nombre })} />
                        </td>
                      );
                    })}
                  </tr>
                ))}
                <tr style={{ background: '#f0fdf4' }}>
                  <td style={{ ...S.td, fontWeight: 700, color: '#16a34a', textAlign: 'center' }}>E</td>
                  <td colSpan={5} style={{ ...S.td, color: '#166534', fontSize: '13px' }}>
                    <i className="ti ti-leaf" style={{ marginRight: '6px' }}></i>
                    {ensalada?.nombre || 'Ensalada'} — siempre disponible (sin programación)
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <button onClick={handleGuardar} disabled={guardando || modificados.length === 0}
            style={{ ...S.btn, background: '#0086ab', color: 'white', padding: '10px 24px', fontSize: '14px', opacity: guardando || modificados.length === 0 ? 0.6 : 1,
              cursor: guardando || modificados.length === 0 ? 'default' : 'pointer' }}>
            {guardando ? 'Guardando...' : (modificados.length === 0 ? 'Sin cambios por guardar' : `Guardar cambios (${modificados.length} día${modificados.length !== 1 ? 's' : ''})`)}
          </button>
        </>
      )}

      {alta && (
        <ModalPlatillo
          platillo={null}
          nombreInicial={alta.nombre}
          defaults={defaultsPlatillo(catalogo)}
          onClose={() => setAlta(null)}
          onGuardar={creado => {
            // El platillo ya existe en el catálogo: se agrega a la lista local y queda elegido en la celda que lo pidió.
            setCatalogo(prev => prev.concat([creado]));
            ponerOpcion(alta.fecha, alta.n, creado);
          }}
        />
      )}
    </div>
  );
}

// Aviso fijo en los reportes heredados que NO se adaptaron al padrón temporal de comedor (ver
// scripts/padron-comedor/LEEME.md). No cambia lo que calculan: sólo evita confundirlos con los reportes adaptados.
export function AvisoReporteNoAdaptado({ children }) {
  return (
    <div role="note" data-testid="aviso-no-adaptado" style={{ background: '#fffbeb', border: '1px solid #fcd34d', color: '#92400e', borderRadius: '8px', padding: '10px 14px', fontSize: '12px', marginBottom: '16px' }}>
      <b>Reporte no adaptado al padrón temporal de comedor.</b> {children} Para el descuento usa <b>Descuento Intellisis</b>.
    </div>
  );
}

// ── Tab Control Semanal ────────────────────────────────────────────────────────
function TabControlSemanal() {
  const [semana, setSemana] = useState(toYMD(getLunes()));
  const [control, setControl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalAjustarDescuentos, setModalAjustarDescuentos] = useState(false);
  const [idEmpleadoBusq, setIdEmpleadoBusq] = useState('');
  const [estadoNuevo, setEstadoNuevo] = useState('PENDIENTE');

  useEffect(() => {
    const cargar = async () => {
      setLoading(true);
      const data = await apiComedor('api/comedor/control-semanal', null, { semana_inicio: semana });
      if (data.success) setControl(data);
      setLoading(false);
    };
    cargar();
  }, [semana]);

  const moverSemana = dir => {
    const d = new Date(semana + 'T12:00:00');
    d.setDate(d.getDate() + dir * 7);
    setSemana(toYMD(d));
  };

  if (!control || loading) return <div style={{ padding: '24px', color: '#9ca3af' }}>Cargando...</div>;

  const rows = [
    { label: 'Activos', cantidad: control.activos.cantidad, subtotal: control.activos.subtotal, descuento: true },
    { label: 'Bajas', cantidad: control.bajas.cantidad, subtotal: control.bajas.subtotal, descuento: false },
    { label: 'Externos (efectivo)', cantidad: control.externos.cantidad, subtotal: control.externos.subtotal, descuento: false },
    { label: 'Platillos Soporte', cantidad: control.soporte.cantidad, subtotal: control.soporte.subtotal, descuento: false },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#1d3d6b' }}>Control Semanal</h2>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <button onClick={() => moverSemana(-1)} style={{ ...S.btn, background: '#f3f4f6', padding: '6px 12px' }}>‹</button>
          <span style={{ fontWeight: 600, color: '#1d3d6b', minWidth: '140px', textAlign: 'center' }}>Semana {semanaLabel(semana)}</span>
          <button onClick={() => moverSemana(1)} style={{ ...S.btn, background: '#f3f4f6', padding: '6px 12px' }}>›</button>
        </div>
      </div>
      <AvisoReporteNoAdaptado>
        Clasifica con una hoja que no existe (COMEDOR_EMPLEADOS): todos los pedidos aparecen como «Activos», sin distinguir bajas ni externos.
        Su «Descargar Excel» busca en EMPLEADOS: a quien sólo está en el padrón lo muestra como «SOPORTE», y el archivo queda visible para cualquiera con el enlace.
      </AvisoReporteNoAdaptado>

      <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
          <thead>
            <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#1d3d6b' }}>Categoría</th>
              <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, color: '#1d3d6b' }}>Cantidad</th>
              <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600, color: '#1d3d6b' }}>Subtotal</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} style={{ borderBottom: i < rows.length - 1 ? '1px solid #f3f4f6' : 'none', background: row.descuento ? '#f0fdf4' : 'white' }}>
                <td style={{ padding: '12px 16px', color: '#374151' }}>{row.label}</td>
                <td style={{ padding: '12px 16px', textAlign: 'center', color: '#374151', fontWeight: 500 }}>{row.cantidad}</td>
                <td style={{ padding: '12px 16px', textAlign: 'right', color: '#1d3d6b', fontWeight: 600 }}>${row.subtotal.toFixed(2)}</td>
              </tr>
            ))}
            <tr style={{ background: '#eff6ff', fontWeight: 700, borderTop: '2px solid #0086ab' }}>
              <td style={{ padding: '12px 16px', color: '#0086ab' }}>TOTAL</td>
              <td style={{ padding: '12px 16px', textAlign: 'center', color: '#0086ab' }}>{control.total.cantidad}</td>
              <td style={{ padding: '12px 16px', textAlign: 'right', color: '#0086ab' }}>${control.total.subtotal.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        <button onClick={() => {
          apiComedor('api/comedor/marcar-descuentos', null, { semana_inicio: semana }).then(data => {
            if (data.success) {
              alert(`✓ Descuentos marcados\n\n${data.actualizado} descuentos actualizados\n\nCambio pendiente creado: #${data.id_cambio}\n\nAcción: Confirmar en Cambios Pendientes`);
            } else {
              alert('Error: ' + (data.error || 'Error al marcar descuentos'));
            }
          });
        }} style={{ ...S.btn, background: '#16a34a', color: 'white' }}>✓ Marcar Descuentos para Nómina</button>
        <button onClick={() => setModalAjustarDescuentos(true)} style={{ ...S.btn, background: '#f59e0b', color: 'white', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <i className="ti ti-pencil"></i> Ajustar Descuentos
        </button>
        <button onClick={() => {
          apiComedor('api/comedor/control-semanal-detallado', null, { semana_inicio: semana }).then(data => {
            if (data.success && data.url) window.open(data.url, '_blank');
            else alert(data.error || 'Error al descargar');
          });
        }} style={{ ...S.btn, background: '#0086ab', color: 'white' }}>⬇ Descargar Excel</button>
      </div>

      {modalAjustarDescuentos && (
        <div style={S.overlay}>
          <div style={{ ...S.modal, width: '500px' }}>
            <h2 style={{ fontSize: '16px', fontWeight: 700, color: '#1d3d6b', marginBottom: '20px' }}>Ajustar Estado de Descuento</h2>

            <div style={{ marginBottom: '16px' }}>
              <label style={S.label}>ID o # Empleado a buscar</label>
              <input type="text" style={S.input} value={idEmpleadoBusq} onChange={e => setIdEmpleadoBusq(e.target.value)}
                placeholder="Ej: 12658 o E001" />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={S.label}>Nuevo estado</label>
              <select style={{ ...S.input, background: 'white' }} value={estadoNuevo} onChange={e => setEstadoNuevo(e.target.value)}>
                <option value="NO_APLICA">NO_APLICA (Soporte)</option>
                <option value="PENDIENTE">PENDIENTE (Por descontar)</option>
                <option value="APLICADO">APLICADO (Descuento marcado)</option>
                <option value="CONFIRMADO">CONFIRMADO (Confirmado Intellisis)</option>
                <option value="BAJA_PENDIENTE">BAJA_PENDIENTE (En finiquito)</option>
                <option value="EXTERNO_PENDIENTE">EXTERNO_PENDIENTE (Por cobrar)</option>
                <option value="EXTERNO_PAGADO">EXTERNO_PAGADO (Cobrado)</option>
              </select>
            </div>

            <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '6px', padding: '12px', marginBottom: '16px', fontSize: '12px', color: '#92400e' }}>
              ⚠️ Esta acción se registrará en bitácora. Solo cambiar si es corrección manual.
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setModalAjustarDescuentos(false)} style={{ ...S.btn, background: '#f3f4f6', color: '#374151' }}>Cancelar</button>
              <button onClick={() => {
                if (!idEmpleadoBusq) { alert('Ingresa ID de empleado'); return; }
                // Aquí se llamará al endpoint para actualizar
                alert('Funcionalidad en desarrollo: ' + idEmpleadoBusq + ' → ' + estadoNuevo);
                setModalAjustarDescuentos(false);
              }} style={{ ...S.btn, background: '#16a34a', color: 'white' }}>Confirmar Cambio</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Tab Efectivo Externos ────────────────────────────────────────────────────
function TabEfectivoExternos() {
  const [semana, setSemana] = useState(toYMD(getLunes()));
  const [externos, setExternos] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const cargar = async () => {
      setLoading(true);
      const data = await apiComedor('api/comedor/externos-semana', null, { semana_inicio: semana });
      if (data.success) setExternos(data);
      setLoading(false);
    };
    cargar();
  }, [semana]);

  const moverSemana = dir => {
    const d = new Date(semana + 'T12:00:00');
    d.setDate(d.getDate() + dir * 7);
    setSemana(toYMD(d));
  };

  if (!externos || loading) return <div style={{ padding: '24px', color: '#9ca3af' }}>Cargando...</div>;

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#1d3d6b' }}>Efectivo Externos</h2>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <button onClick={() => moverSemana(-1)} style={{ ...S.btn, background: '#f3f4f6', padding: '6px 12px' }}>‹</button>
          <span style={{ fontWeight: 600, color: '#1d3d6b', minWidth: '140px', textAlign: 'center' }}>Semana {semanaLabel(semana)}</span>
          <button onClick={() => moverSemana(1)} style={{ ...S.btn, background: '#f3f4f6', padding: '6px 12px' }}>›</button>
        </div>
      </div>
      <AvisoReporteNoAdaptado>
        Identifica a los externos con una hoja que no existe (COMEDOR_EMPLEADOS), por lo que no refleja a las personas del padrón.
        Su «Descargar Excel» queda visible para cualquiera con el enlace.
      </AvisoReporteNoAdaptado>

      <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden', marginBottom: '20px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#1d3d6b' }}>Empleado</th>
              <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, color: '#1d3d6b' }}>Comidas</th>
              <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600, color: '#1d3d6b' }}>A Cobrar</th>
              <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, color: '#1d3d6b' }}>Estado</th>
            </tr>
          </thead>
          <tbody>
            {externos.externos.map((ext, i) => (
              <tr key={i} style={{ borderBottom: i < externos.externos.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ fontWeight: 500, color: '#1d3d6b' }}>{ext.nombre_empleado}</div>
                  <div style={{ fontSize: '11px', color: '#9ca3af' }}>#{ext.id_empleado} • {expandirSucursal(ext.sucursal)}</div>
                </td>
                <td style={{ padding: '12px 16px', textAlign: 'center', color: '#374151' }}>{ext.cantidad}</td>
                <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600, color: '#1d3d6b' }}>${ext.subtotal.toFixed(2)}</td>
                <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                  <span style={{ fontSize: '11px', background: ext.pagado ? '#d1fae5' : '#fef2f2', color: ext.pagado ? '#065f46' : '#7c2d12', padding: '2px 8px', borderRadius: '4px' }}>
                    {ext.pagado ? '✓ Pagado' : '⚠ Pendiente'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ background: '#eff6ff', borderRadius: '12px', padding: '16px', border: '1px solid #bfdbfe', marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600, fontSize: '15px' }}>
          <span style={{ color: '#1d3d6b' }}>Total Semana</span>
          <span style={{ color: '#0086ab' }}>{externos.total.cantidad} comidas = ${externos.total.subtotal.toFixed(2)}</span>
        </div>
      </div>

      <button onClick={() => {
        apiComedor('api/comedor/excel', { semana_inicio: semana, tipo: 'externos' }).then(data => {
          if (data.success) descargarPDF(data.base64, data.nombre);
        });
      }} style={{ ...S.btn, background: '#0086ab', color: 'white' }}>⬇ Descargar Excel</button>
    </div>
  );
}

const SIN_SUCURSAL = '__sin_sucursal__';
function TabHistorial() {
  const hoy = toYMD(new Date());
  const primerDiaMes = (() => { const d = new Date(); d.setDate(1); return toYMD(d); })();
  const [desdeDate, setDesdeDate] = useState(primerDiaMes);
  const [hastaDate, setHastaDate] = useState(hoy);
  const [empleadoBusq, setEmpleadoBusq] = useState('');
  const [platilloBusq, setPlatilloBusq] = useState('');
  const [sucursalFiltro, setSucursalFiltro] = useState('');
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagina, setPagina] = useState(0);
  const POR_PAG = 50;

  const cargar = async () => {
    setLoading(true); setPagina(0);
    try {
      const data = await apiComedor('api/comedor/historial', null, { fecha_desde: desdeDate, fecha_hasta: hastaDate });
      if (data.success) setPedidos(data.pedidos || []);
    } finally {
      setLoading(false);
    }
  };

  const filtrados = pedidos.filter(p =>
    (!empleadoBusq || (p.nombre_empleado || '').toLowerCase().includes(empleadoBusq.toLowerCase())) &&
    (!platilloBusq || (p.nombre_platillo || '').toLowerCase().includes(platilloBusq.toLowerCase())) &&
    (!sucursalFiltro || (sucursalFiltro === SIN_SUCURSAL ? !String(p.sucursal || '').trim() : p.sucursal === sucursalFiltro))
  );

  const totalPags = Math.ceil(filtrados.length / POR_PAG);
  const paginados = filtrados.slice(pagina * POR_PAG, (pagina + 1) * POR_PAG);
  const sucursales = [...new Set(pedidos.map(p => p.sucursal).filter(Boolean))].sort();

  const handleExportar = async () => {
    const data = await apiComedor('api/comedor/excel', { fecha_desde: desdeDate, fecha_hasta: hastaDate });
    if (data.success && data.url) window.open(data.url, '_blank');
    else alert(data.error || 'Error al exportar Excel.');
  };

  const handleExportarControlSemanal = async () => {
    const data = await apiComedor('api/comedor/control-semanal-excel', null, { fecha_desde: desdeDate, fecha_hasta: hastaDate });
    if (data.success && data.url) window.open(data.url, '_blank');
    else alert(data.error || 'Error al exportar Control Semanal.');
  };

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#1d3d6b' }}>Historial de Pedidos</h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={handleExportar}
            style={{ ...S.btn, background: '#16a34a', color: 'white', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <i className="ti ti-file-spreadsheet"></i> Exportar Excel
          </button>
          <button onClick={handleExportarControlSemanal}
            style={{ ...S.btn, background: '#0086ab', color: 'white', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <i className="ti ti-file-spreadsheet"></i> Exportar Control Semanal
          </button>
        </div>
      </div>
      <AvisoReporteNoAdaptado>
        «Exportar Control Semanal» busca en EMPLEADOS: a quien sólo está en el padrón lo muestra como «Desconocido» (su consumo sí se cuenta).
        Los dos Excel de esta pantalla quedan visibles para cualquiera con el enlace. La tabla de abajo sí está adaptada.
      </AvisoReporteNoAdaptado>

      {/* Filtros */}
      <div style={{ ...S.card, marginBottom: '16px' }}>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label style={S.label}>Desde</label>
            <input type="date" style={{ ...S.input, width: 'auto' }} value={desdeDate} onChange={e => setDesdeDate(e.target.value)} />
          </div>
          <div>
            <label style={S.label}>Hasta</label>
            <input type="date" style={{ ...S.input, width: 'auto' }} value={hastaDate} onChange={e => setHastaDate(e.target.value)} />
          </div>
          <div>
            <label style={S.label}>Empleado</label>
            <input style={{ ...S.input, width: '180px' }} value={empleadoBusq} onChange={e => { setEmpleadoBusq(e.target.value); setPagina(0); }} placeholder="Nombre..." />
          </div>
          <div>
            <label style={S.label}>Platillo</label>
            <input style={{ ...S.input, width: '150px' }} value={platilloBusq} onChange={e => { setPlatilloBusq(e.target.value); setPagina(0); }} placeholder="Nombre..." />
          </div>
          <div>
            <label style={S.label}>Sucursal laboral</label>
            <select aria-label="Sucursal laboral" style={{ ...S.input, width: '170px', background: 'white' }} value={sucursalFiltro} onChange={e => { setSucursalFiltro(e.target.value); setPagina(0); }}>
              <option value="">Todas</option>
              {sucursales.map(s => <option key={s} value={s}>{s}</option>)}
              {pedidos.some(p => !String(p.sucursal || '').trim()) && <option value={SIN_SUCURSAL}>Sin sucursal laboral</option>}
            </select>
          </div>
          <button onClick={cargar} style={{ ...S.btn, background: '#0086ab', color: 'white' }}>
            <i className="ti ti-search" style={{ marginRight: '6px' }}></i>Buscar
          </button>
        </div>
      </div>

      {filtrados.length > 0 && (
        <div style={{ marginBottom: '10px', fontSize: '13px', color: '#6b7280' }}>
          {filtrados.length} pedido{filtrados.length !== 1 ? 's' : ''} encontrado{filtrados.length !== 1 ? 's' : ''}
          {totalPags > 1 && ` · Página ${pagina + 1} de ${totalPags}`}
        </div>
      )}

      <div style={{ ...S.card, padding: 0, overflow: 'hidden', marginBottom: '16px' }}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>Cargando...</div>
        ) : paginados.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
            {pedidos.length === 0 ? 'Selecciona un rango de fechas y presiona Buscar.' : 'Sin resultados con los filtros aplicados.'}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#f9fafb' }}>
              <tr>
                <th style={S.th}>Fecha entrega</th>
                <th style={S.th}>Empleado</th>
                <th style={S.th}>Destino de entrega</th>
                <th style={S.th}>Sucursal laboral</th>
                <th style={S.th}>Platillo</th>
                <th style={S.th}>Estado</th>
                <th style={S.th}>Hora pedido</th>
              </tr>
            </thead>
            <tbody>
              {paginados.map(p => (
                <tr key={p.id_pedido} style={{ opacity: p.estado === 'cancelado' ? 0.6 : 1 }}>
                  <td style={S.td}>{fmtFechaCorta(String(p.fecha_entrega).slice(0, 10))}</td>
                  <td style={S.td}>{p.nombre_empleado}</td>
                  <td style={S.td}>{p.ubicacion_comedor || <span style={{ color: '#9ca3af' }}>—</span>}</td>
                  <td style={S.td}>{p.sucursal || <span style={{ color: '#9ca3af' }}>—</span>}</td>
                  <td style={{ ...S.td, textDecoration: p.estado === 'cancelado' ? 'line-through' : 'none' }}>{p.nombre_platillo}</td>
                  <td style={S.td}>
                    <span style={{ background: p.estado === 'activo' ? '#dcfce7' : '#f3f4f6',
                      color: p.estado === 'activo' ? '#166534' : '#6b7280',
                      padding: '2px 8px', borderRadius: '99px', fontSize: '11px', fontWeight: 600 }}>
                      {p.estado}
                    </span>
                  </td>
                  <td style={S.td}><span style={{ fontSize: '11px', color: '#9ca3af' }}>{String(p.timestamp || '').slice(11, 16)}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {totalPags > 1 && (
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
          <button onClick={() => setPagina(p => Math.max(0, p - 1))} disabled={pagina === 0}
            style={{ ...S.btn, background: '#f3f4f6', color: '#374151', opacity: pagina === 0 ? 0.5 : 1 }}>‹ Anterior</button>
          <span style={{ padding: '8px 12px', fontSize: '13px', color: '#6b7280' }}>{pagina + 1} / {totalPags}</span>
          <button onClick={() => setPagina(p => Math.min(totalPags - 1, p + 1))} disabled={pagina >= totalPags - 1}
            style={{ ...S.btn, background: '#f3f4f6', color: '#374151', opacity: pagina >= totalPags - 1 ? 0.5 : 1 }}>Siguiente ›</button>
        </div>
      )}
    </div>
  );
}
// ── Padrón temporal de comedor ───────────────────────────────────────────────
// Alta manual (desde la captura) o destino faltante. Dar de alta NO registra comida: la comida se captura después con
// los controles de siempre. El backend es idempotente por ID (reintentar no duplica) y no sobrescribe un nombre distinto.
export function ModalPersonaPadron({ usuario, modo, persona, inicial, onCerrar, onLista }) {
  const [id, setId] = useState(persona?.id_empleado || inicial?.id_empleado || '');
  const [nombre, setNombre] = useState(persona?.nombre_completo || inicial?.nombre_completo || '');
  const [destino, setDestino] = useState(persona?.area_comedor || '');
  const [confirmar, setConfirmar] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const esAlta = modo === 'alta';
  const idTexto = id.trim(), nombreLimpio = nombrePersona(nombre);
  const valido = (!esAlta || (idPadronValido(idTexto) && nombreLimpio)) && AREAS_COMEDOR.includes(destino);

  const enviar = async () => {
    setEnviando(true); setResultado(null);
    let r = null;
    try {
      r = esAlta
        ? await escribirPadron(usuario, 'api/comedor/padron/alta', { id_empleado: idTexto, nombre_completo: nombreLimpio, area_comedor: destino })
        : await escribirPadron(usuario, 'api/comedor/padron/actualizar', { id_empleado: persona.id_empleado,
          esperado: { nombre_completo: persona.nombre_completo, area_comedor: persona.area_comedor, habilitado: persona.habilitado }, cambios: { area_comedor: destino } });
    } catch (e) { r = { success: false, error: 'Sin respuesta del servidor (' + e.message + '). Puedes reintentar: no se duplica.' }; }
    setEnviando(false);
    if (r && r.success && r.persona && (r.resultado !== 'YA_EXISTE' || (r.persona.habilitado && r.persona.area_comedor))) {
      if (r.resultado === 'YA_EXISTE') { setResultado({ tipo: 'existe', persona: r.persona }); return; }
      onLista(r.persona); return;
    }
    setConfirmar(false);   // vuelve a la edición conservando lo capturado
    if (r && r.resultado === 'YA_EXISTE') { setResultado({ tipo: 'error', texto: `El ID ${r.persona.id_empleado} ya está en el padrón (${r.persona.nombre_completo}) pero ${r.persona.habilitado ? 'no tiene destino de entrega' : 'está deshabilitado'}: revísalo en la pestaña Padrón.` }); return; }
    if (r && r.codigo === 'CONFLICTO_NOMBRE') { setResultado({ tipo: 'error', texto: `El ID ${r.persona.id_empleado} ya está registrado como «${r.persona.nombre_completo}». No se sobrescribió: revisa el ID o el nombre.` }); return; }
    setResultado({ tipo: 'error', texto: r && r.codigo === 'NO_AUTORIZADO' ? MENSAJE_NO_AUTORIZADO : (r && r.error) || 'No se pudo guardar' });
  };

  return (
    <div style={S.overlay}>
      <div style={S.modal} role="dialog" aria-label={esAlta ? 'Agregar persona al padrón' : 'Completar destino de entrega'}>
        <h2 style={{ fontSize: '16px', fontWeight: 700, color: '#1d3d6b', marginBottom: '6px' }}>{esAlta ? 'Agregar persona al padrón' : 'Completar destino de entrega'}</h2>
        <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '16px' }}>
          {esAlta ? 'Usa el ID oficial (5 dígitos, con sus ceros). Agregar a la persona no registra ninguna comida.' : 'Antes de registrar su comida, indica dónde se le entrega.'}
          {' '}El destino es sólo el lugar de entrega de la comida; no es su sucursal ni su área laboral.
        </p>
        {resultado?.tipo === 'existe' ? (
          <div>
            <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '12px', fontSize: '13px', marginBottom: '16px' }}>
              Ya está en el padrón: <b>#{resultado.persona.id_empleado} {resultado.persona.nombre_completo}</b> · Entrega {resultado.persona.area_comedor}
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={onCerrar} style={{ ...S.btn, background: '#f3f4f6', color: '#374151' }}>Cerrar</button>
              <button onClick={() => onLista(resultado.persona)} style={{ ...S.btn, background: '#0086ab', color: 'white' }}>Usar este registro</button>
            </div>
          </div>
        ) : (
          <>
            {esAlta ? (
              <>
                <label style={S.label}>ID oficial</label>
                <input aria-label="ID oficial" style={{ ...S.input, marginBottom: '4px', fontFamily: 'monospace' }} value={id} disabled={confirmar} onChange={e => setId(e.target.value)} placeholder="00418" inputMode="numeric" />
                {idTexto && !idPadronValido(idTexto) && <div style={{ fontSize: '11px', color: '#b91c1c', marginBottom: '8px' }}>Debe tener exactamente 5 dígitos (con sus ceros) y no ser 00000. No se agregan ceros automáticamente.</div>}
                <label style={{ ...S.label, marginTop: '10px' }}>Nombre completo</label>
                <input aria-label="Nombre completo" style={{ ...S.input, marginBottom: '12px' }} value={nombre} disabled={confirmar} onChange={e => setNombre(e.target.value)} />
              </>
            ) : (
              <div style={{ fontSize: '13px', marginBottom: '12px' }}><b>#{persona.id_empleado}</b> {persona.nombre_completo}</div>
            )}
            <label style={S.label}>Destino de entrega</label>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
              {AREAS_COMEDOR.map(a => (
                <button key={a} disabled={confirmar} onClick={() => setDestino(a)} aria-pressed={destino === a}
                  style={{ ...S.btn, background: destino === a ? '#0086ab' : '#f3f4f6', color: destino === a ? 'white' : '#374151' }}>{a}</button>
              ))}
            </div>
            {confirmar && (
              <div data-testid="confirmar-persona" style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '8px', padding: '12px', fontSize: '13px', marginBottom: '16px', color: '#92400e' }}>
                Confirma los datos: <b>#{esAlta ? idTexto : persona.id_empleado}</b> {esAlta ? nombreLimpio : persona.nombre_completo} · Entrega <b>{destino}</b>
              </div>
            )}
            {resultado?.tipo === 'error' && <div role="alert" style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: '8px', padding: '10px 12px', fontSize: '12px', marginBottom: '12px' }}>{resultado.texto}</div>}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={confirmar ? () => setConfirmar(false) : onCerrar} disabled={enviando} style={{ ...S.btn, background: '#f3f4f6', color: '#374151' }}>{confirmar ? 'Corregir' : 'Cancelar'}</button>
              {!confirmar
                ? <button onClick={() => { setResultado(null); setConfirmar(true); }} disabled={!valido} style={{ ...S.btn, background: '#0086ab', color: 'white', opacity: valido ? 1 : 0.5 }}>Continuar</button>
                : <button onClick={enviar} disabled={enviando} style={{ ...S.btn, background: '#16a34a', color: 'white', opacity: enviando ? 0.7 : 1 }}>{enviando ? 'Guardando…' : 'Confirmar y guardar'}</button>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Pestaña Padrón: consulta, importación con vista previa (el backend clasifica sin escribir y vuelve a validar al
// confirmar), edición de nombre o destino y habilitar/deshabilitar. Las escrituras exigen sesión autorizada en el backend.
export function TabPadron({ usuario }) {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState(null);
  const [busq, setBusq] = useState('');
  const [filtro, setFiltro] = useState('todas');
  const [editar, setEditar] = useState(null);
  const [alta, setAlta] = useState(null);
  const [confirmarEstado, setConfirmarEstado] = useState(null);
  const [imp, setImp] = useState(null);
  const [ocupado, setOcupado] = useState('');

  const cargar = useCallback(async () => {
    setError('');
    try {
      const [p, e] = await Promise.all([apiComedor('api/comedor/personas'), apiComedor('api/empleados')]);
      if (!p || !p.success) throw new Error((p && p.error) || 'No se pudo leer el padrón');
      setDatos({ ...p, activosEmpleados: empleadosActivosComedor(e && e.empleados) });
    } catch (err) { setError(err.message); }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const respuestaError = r => (r && r.codigo === 'NO_AUTORIZADO' ? MENSAJE_NO_AUTORIZADO : (r && r.error) || 'Sin respuesta del servidor');

  const elegirArchivo = async archivo => {
    setMensaje(null); setImp(null);
    if (!archivo) return;
    const bytes = await new Promise((ok, mal) => { const fr = new FileReader(); fr.onload = () => ok(new Uint8Array(fr.result)); fr.onerror = () => mal(fr.error); fr.readAsArrayBuffer(archivo); });
    const lectura = leerArchivoPadron(bytes, XLSX);
    if (lectura.error) { setImp({ nombre: archivo.name, error: lectura.error }); return; }
    setOcupado('Revisando el archivo…');
    let r = null;
    try { r = await escribirPadron(usuario, 'api/comedor/padron/importar', { modo: 'previsualizar', lote: archivo.name, registros: lectura.registros }); } catch (e) { r = null; }
    setOcupado('');
    if (!r || !r.success) { setImp({ nombre: archivo.name, error: respuestaError(r) }); return; }
    setImp({ nombre: archivo.name, lectura, previa: r, enterado: false, ausentes: activosAusentes(datos?.activosEmpleados, lectura.registros, datos?.personas) });
  };

  const aplicar = async () => {
    setOcupado('Guardando…');
    let r = null;
    try { r = await escribirPadron(usuario, 'api/comedor/padron/importar', { modo: 'aplicar', lote: imp.nombre, registros: imp.lectura.registros }); } catch (e) { r = null; }
    setOcupado('');
    if (!r || !r.success) { setMensaje({ tipo: 'error', texto: respuestaError(r) + (r ? '' : '. Puedes reintentar: lo ya guardado se reconoce y no se duplica.') }); return; }
    setMensaje({ tipo: 'ok', texto: `Importación guardada: ${r.agregados} persona${r.agregados !== 1 ? 's' : ''} nueva${r.agregados !== 1 ? 's' : ''}, ${r.areas_completadas} destino${r.areas_completadas !== 1 ? 's' : ''} completado${r.areas_completadas !== 1 ? 's' : ''}. Nadie se deshabilitó ni se modificó por no venir en el archivo.` });
    setImp(null); cargar();
  };

  const cambiarEstado = async (p, habilitado) => {
    setOcupado('Guardando…');
    let r = null;
    try { r = await escribirPadron(usuario, 'api/comedor/padron/actualizar', { id_empleado: p.id_empleado, esperado: { nombre_completo: p.nombre_completo, area_comedor: p.area_comedor, habilitado: p.habilitado }, cambios: { habilitado } }); } catch (e) { r = null; }
    setOcupado(''); setConfirmarEstado(null);
    setMensaje(r && r.success ? { tipo: 'ok', texto: `#${p.id_empleado} ${habilitado ? 'habilitada' : 'deshabilitada'}. ${habilitado ? '' : 'Su historial se conserva; ya no puede registrar comidas nuevas.'}` } : { tipo: 'error', texto: respuestaError(r) });
    cargar();
  };

  const personas = datos?.personas || [];
  const q = busq.trim().toLowerCase();
  const visibles = personas.filter(p => (filtro === 'todas' || (filtro === 'habilitadas' && p.habilitado) || (filtro === 'deshabilitadas' && !p.habilitado) || (filtro === 'sin_destino' && !p.area_comedor))
    && (!q || p.id_empleado.includes(q) || p.nombre_completo.toLowerCase().includes(q)));
  const cuenta = f => personas.filter(f).length;
  const caja = (fondo, borde, color) => ({ background: fondo, border: `1px solid ${borde}`, color, borderRadius: '8px', padding: '12px 14px', fontSize: '13px', marginBottom: '16px' });
  const problemas = imp?.previa ? imp.previa.items.filter(it => !ESTADOS_SIN_PROBLEMA.includes(it.estado)) : [];
  const avisosArea = imp?.previa ? imp.previa.items.filter(it => it.estado === 'SIN_CAMBIOS' && it.area_distinta) : [];
  const ajustes = imp?.previa ? imp.previa.items.filter(it => it.nombre_ajustado) : [];
  const porGuardar = imp?.previa ? imp.previa.items.filter(it => ESTADOS_QUE_SE_GUARDAN.includes(it.estado)).length : 0;

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '16px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#1d3d6b' }}>Padrón de comedor</h2>
        <p style={{ color: '#6b7280', fontSize: '13px', marginTop: '2px' }}>Padrón temporal e independiente de EMPLEADOS · el destino es sólo el lugar de entrega (NAVE 1 o NAVE 2)</p>
      </div>
      {error && <div role="alert" style={caja('#fef2f2', '#fecaca', '#b91c1c')}>⚠️ {error}</div>}
      {mensaje && <div role={mensaje.tipo === 'error' ? 'alert' : 'status'} style={mensaje.tipo === 'error' ? caja('#fef2f2', '#fecaca', '#b91c1c') : caja('#f0fdf4', '#bbf7d0', '#166534')}>{mensaje.texto}</div>}
      {datos && (
        <div data-testid="fuente" style={caja(datos.fuente === 'PADRON' ? '#eff6ff' : '#f9fafb', datos.fuente === 'PADRON' ? '#bfdbfe' : '#e5e7eb', '#1e3a8a')}>
          Fuente activa para la captura: <b>{datos.fuente === 'PADRON' ? 'padrón de comedor' : 'EMPLEADOS'}</b>
          {datos.fuente !== 'PADRON' && ' — el padrón todavía no se usa para buscar ni validar personas.'}
          {!datos.padron_disponible && <div style={{ marginTop: '6px', color: '#b91c1c' }}>El padrón no está disponible: {datos.motivo}</div>}
          {(datos.filas_invalidas > 0 || (datos.ids_repetidos || []).length > 0) && <div style={{ marginTop: '6px', color: '#b91c1c' }}>Revisa la hoja: {datos.filas_invalidas} fila(s) con ID inválido y {(datos.ids_repetidos || []).length} ID(s) repetido(s) que no se usan.</div>}
        </div>
      )}

      {datos?.padron_disponible && (
        <div style={{ ...S.card, marginBottom: '16px' }}>
          <div style={{ fontWeight: 600, color: '#1d3d6b', marginBottom: '8px' }}>Importar lista</div>
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '10px' }}>Excel con columnas ID, NOMBRE_COMPLETO y AREA. Primero se muestra una vista previa; nada se guarda hasta confirmar. Reimportar no duplica, no sobrescribe nombres y no da de baja a quien no venga en el archivo.</div>
          <input type="file" accept=".xlsx,.xls" aria-label="Archivo del padrón" disabled={!!ocupado} onChange={e => { elegirArchivo(e.target.files[0]); e.target.value = ''; }} />
          {ocupado && <div style={{ marginTop: '8px', fontSize: '12px', color: '#6b7280' }}>{ocupado}</div>}
          {imp?.error && <div role="alert" style={{ ...caja('#fef2f2', '#fecaca', '#b91c1c'), marginTop: '12px' }}>{imp.nombre}: {imp.error}</div>}
          {imp?.previa && (
            <div data-testid="vista-previa" style={{ marginTop: '14px' }}>
              <div style={{ fontSize: '13px', marginBottom: '10px' }}>
                <b>{imp.nombre}</b>{imp.lectura.otrasHojas.length ? ` (se leyó la hoja «${imp.lectura.hoja}»; se ignoran ${imp.lectura.otrasHojas.length} hoja(s) más)` : ''}: {imp.previa.resumen.total} registros.
              </div>
              <table style={{ borderCollapse: 'collapse', fontSize: '13px', marginBottom: '12px' }}>
                <tbody>
                  {Object.keys(ETIQUETA_ESTADO_IMPORTACION).filter(k => imp.previa.resumen[k]).map(k => (
                    <tr key={k}><td style={{ padding: '3px 12px 3px 0', color: ESTADOS_SIN_PROBLEMA.includes(k) ? '#374151' : '#b91c1c' }}>{ETIQUETA_ESTADO_IMPORTACION[k]}</td><td style={{ fontWeight: 700 }}>{imp.previa.resumen[k]}</td></tr>
                  ))}
                  <tr><td style={{ padding: '3px 12px 3px 0', color: '#374151' }}>Nuevos por destino</td><td>{Object.entries(imp.previa.resumen.por_area || {}).map(([a, n]) => `${a}: ${n}`).join(' · ') || '—'}</td></tr>
                  <tr><td style={{ padding: '3px 12px 3px 0', color: '#374151' }}>Nombres con espacios ajustados</td><td>{imp.previa.resumen.nombres_ajustados}</td></tr>
                </tbody>
              </table>
              {problemas.length > 0 && (
                <div style={caja('#fef2f2', '#fecaca', '#991b1b')}>
                  <b>Problemas por resolver</b> (no se guardan; corrige el archivo o revisa el padrón):
                  <ul style={{ margin: '6px 0 0 18px' }}>{problemas.map((it, i) => <li key={i}>Fila {it.fila} · ID {String(it.id_empleado === null || it.id_empleado === undefined ? '' : it.id_empleado) || '(vacío)'}: {ETIQUETA_ESTADO_IMPORTACION[it.estado]}{it.nombre_en_padron ? ` — en el padrón: «${it.nombre_en_padron}», en el archivo: «${it.nombre_completo}»` : ''}{it.detalle ? ` (${it.detalle})` : ''}</li>)}</ul>
                </div>
              )}
              {ajustes.length > 0 && <div style={caja('#fffbeb', '#fcd34d', '#92400e')}><b>Nombres con espacios ajustados</b> (sólo espacios; acentos y demás caracteres intactos):<ul style={{ margin: '6px 0 0 18px' }}>{ajustes.map((it, i) => <li key={i}>Fila {it.fila} · ID {it.id_empleado}: «{it.nombre_completo}»</li>)}</ul></div>}
              {avisosArea.length > 0 && <div style={caja('#fffbeb', '#fcd34d', '#92400e')}><b>Destino distinto al del padrón</b> (no se cambia al importar; edítalo en la lista si corresponde): {avisosArea.map(it => `${it.id_empleado} (padrón ${it.area_en_padron}, archivo ${it.area_comedor})`).join(', ')}</div>}
              {imp.ausentes.length > 0 && <div data-testid="ausentes" style={caja('#eff6ff', '#bfdbfe', '#1e3a8a')}><b>Aviso:</b> {imp.ausentes.length} activo(s) de EMPLEADOS no vienen en el archivo ni están en el padrón. No se agregan solos; si corresponde, dalos de alta manualmente: {imp.ausentes.join(', ')}</div>}
              <label style={{ display: 'flex', gap: '8px', alignItems: 'center', margin: '6px 0 12px', fontSize: '13px', cursor: 'pointer' }}>
                <input type="checkbox" checked={imp.enterado} onChange={e => setImp({ ...imp, enterado: e.target.checked })} /> Revisé la vista previa
              </label>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={() => setImp(null)} disabled={!!ocupado} style={{ ...S.btn, background: '#f3f4f6', color: '#374151' }}>Descartar</button>
                <button onClick={aplicar} disabled={!imp.enterado || !porGuardar || !!ocupado} style={{ ...S.btn, background: '#16a34a', color: 'white', opacity: imp.enterado && porGuardar ? 1 : 0.5 }}>
                  {porGuardar ? `Guardar ${porGuardar} cambio${porGuardar !== 1 ? 's' : ''} en el padrón` : 'Nada que guardar'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {datos?.padron_disponible && (
        <>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '10px' }}>
            <input style={{ ...S.input, width: '260px' }} value={busq} onChange={e => setBusq(e.target.value)} placeholder="Buscar por ID o nombre..." aria-label="Buscar en el padrón" />
            {[['todas', 'Todas', () => true], ['habilitadas', 'Habilitadas', p => p.habilitado], ['deshabilitadas', 'Deshabilitadas', p => !p.habilitado], ['sin_destino', 'Sin destino', p => !p.area_comedor]].map(([k, t, f]) => (
              <button key={k} onClick={() => setFiltro(k)} aria-pressed={filtro === k} style={{ ...S.btn, padding: '6px 12px', background: filtro === k ? '#0086ab' : '#f3f4f6', color: filtro === k ? 'white' : '#374151' }}>{t} ({cuenta(f)})</button>
            ))}
            <span data-testid="por-area" style={{ fontSize: '12px', color: '#6b7280' }}>{AREAS_COMEDOR.map(a => `${a}: ${cuenta(p => p.habilitado && p.area_comedor === a)}`).join(' · ')} habilitadas</span>
            <button onClick={() => { setMensaje(null); setAlta(/^\d+$/.test(busq.trim()) ? { id_empleado: busq.trim() } : {}); }} disabled={!!ocupado}
              style={{ ...S.btn, padding: '6px 12px', background: '#0086ab', color: 'white', marginLeft: 'auto' }}>Agregar persona</button>
          </div>
          <div style={{ ...S.card, padding: 0, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ background: '#f9fafb' }}><tr><th style={S.th}>ID</th><th style={S.th}>Nombre completo</th><th style={S.th}>Destino de entrega</th><th style={S.th}>Estado</th><th style={S.th}>Acciones</th></tr></thead>
              <tbody>
                {visibles.slice(0, 300).map(p => (
                  <tr key={p.id_empleado} data-persona={p.id_empleado} style={{ opacity: p.habilitado ? 1 : 0.6 }}>
                    <td style={{ ...S.td, fontFamily: 'monospace' }}>{p.id_empleado}</td>
                    <td style={S.td}>{p.nombre_completo}</td>
                    <td style={S.td}>{p.area_comedor || <span style={{ color: '#b91c1c' }}>Sin destino</span>}</td>
                    <td style={S.td}>{p.habilitado ? 'Habilitada' : 'Deshabilitada'}</td>
                    <td style={{ ...S.td, display: 'flex', gap: '6px' }}>
                      <button onClick={() => setEditar(p)} style={S.btnEdit}>Editar</button>
                      <button onClick={() => setConfirmarEstado(p)} style={p.habilitado ? S.btnCancel : S.btnSuccess}>{p.habilitado ? 'Deshabilitar' : 'Habilitar'}</button>
                    </td>
                  </tr>
                ))}
                {!visibles.length && <tr><td colSpan={5} style={{ ...S.td, textAlign: 'center', color: '#9ca3af' }}>Sin personas con este filtro.</td></tr>}
              </tbody>
            </table>
            {visibles.length > 300 && <div style={{ padding: '8px 14px', fontSize: '12px', color: '#6b7280' }}>Se muestran 300 de {visibles.length}: usa la búsqueda.</div>}
          </div>
        </>
      )}

      {confirmarEstado && (
        <div style={S.overlay}><div style={{ ...S.modal, width: '440px' }} role="dialog" aria-label="Confirmar estado">
          <p style={{ fontSize: '14px', marginBottom: '16px' }}>
            {confirmarEstado.habilitado ? 'Deshabilitar' : 'Habilitar'} a <b>#{confirmarEstado.id_empleado} {confirmarEstado.nombre_completo}</b>.
            {confirmarEstado.habilitado ? ' Ya no podrá registrar comidas nuevas; su historial se conserva.' : ' Podrá volver a registrar comidas.'}
          </p>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button onClick={() => setConfirmarEstado(null)} style={{ ...S.btn, background: '#f3f4f6', color: '#374151' }}>Cancelar</button>
            <button onClick={() => cambiarEstado(confirmarEstado, !confirmarEstado.habilitado)} disabled={!!ocupado} style={{ ...S.btn, background: confirmarEstado.habilitado ? '#dc2626' : '#16a34a', color: 'white' }}>Confirmar</button>
          </div>
        </div></div>
      )}
      {/* Misma ventana y ruta protegida que en la captura: sólo escribe en el padrón, nunca registra una comida. */}
      {alta && <ModalPersonaPadron usuario={usuario} modo="alta" inicial={alta} onCerrar={() => setAlta(null)}
        onLista={p => { setAlta(null); setMensaje({ tipo: 'ok', texto: `#${p.id_empleado} ${p.nombre_completo} está en el padrón · Entrega ${p.area_comedor}. No se registró ninguna comida.` }); cargar(); }} />}
      {editar && <ModalEditarPersonaPadron usuario={usuario} persona={editar} onCerrar={() => setEditar(null)} onHecho={texto => { setEditar(null); setMensaje({ tipo: 'ok', texto }); cargar(); }} />}
    </div>
  );
}

// Corrige nombre o destino. No modifica pedidos ya guardados: cada uno conserva el nombre y destino de su captura.
export function ModalEditarPersonaPadron({ usuario, persona, onCerrar, onHecho }) {
  const [nombre, setNombre] = useState(persona.nombre_completo);
  const [destino, setDestino] = useState(persona.area_comedor);
  const [confirmar, setConfirmar] = useState(false);
  const [error, setError] = useState('');
  const [enviando, setEnviando] = useState(false);
  const cambios = {};
  if (nombrePersona(nombre) !== persona.nombre_completo) cambios.nombre_completo = nombrePersona(nombre);
  if (destino !== persona.area_comedor) cambios.area_comedor = destino;
  const hay = Object.keys(cambios).length > 0 && nombrePersona(nombre) && AREAS_COMEDOR.includes(destino);
  const enviar = async () => {
    setEnviando(true); setError('');
    let r = null;
    try { r = await escribirPadron(usuario, 'api/comedor/padron/actualizar', { id_empleado: persona.id_empleado, esperado: { nombre_completo: persona.nombre_completo, area_comedor: persona.area_comedor, habilitado: persona.habilitado }, cambios }); } catch (e) { r = null; }
    setEnviando(false);
    if (r && r.success) { onHecho(`#${persona.id_empleado} actualizado. Los pedidos ya guardados no cambian.`); return; }
    setConfirmar(false);
    setError(r && r.codigo === 'NO_AUTORIZADO' ? MENSAJE_NO_AUTORIZADO : r && r.codigo === 'CONFLICTO_VERSION' ? 'Otra persona modificó este registro mientras lo editabas. Cierra y vuelve a abrirlo para ver los datos actuales.' : (r && r.error) || 'Sin respuesta del servidor');
  };
  return (
    <div style={S.overlay}><div style={S.modal} role="dialog" aria-label="Editar persona del padrón">
      <h2 style={{ fontSize: '16px', fontWeight: 700, color: '#1d3d6b', marginBottom: '12px' }}>Editar #{persona.id_empleado}</h2>
      <label style={S.label}>Nombre completo</label>
      <input aria-label="Nombre completo" style={{ ...S.input, marginBottom: '12px' }} value={nombre} disabled={confirmar} onChange={e => setNombre(e.target.value)} />
      <label style={S.label}>Destino de entrega</label>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
        {AREAS_COMEDOR.map(a => <button key={a} disabled={confirmar} onClick={() => setDestino(a)} aria-pressed={destino === a} style={{ ...S.btn, background: destino === a ? '#0086ab' : '#f3f4f6', color: destino === a ? 'white' : '#374151' }}>{a}</button>)}
      </div>
      <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>Los pedidos ya guardados conservan el nombre y el destino con que se capturaron.</p>
      {confirmar && <div data-testid="confirmar-edicion" style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '8px', padding: '10px 12px', fontSize: '13px', marginBottom: '12px', color: '#92400e' }}>
        Confirma: {cambios.nombre_completo !== undefined && <>nombre «{persona.nombre_completo}» → «{cambios.nombre_completo}». </>}{cambios.area_comedor !== undefined && <>destino {persona.area_comedor || '(sin destino)'} → {cambios.area_comedor}.</>}
      </div>}
      {error && <div role="alert" style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: '8px', padding: '10px 12px', fontSize: '12px', marginBottom: '12px' }}>{error}</div>}
      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
        <button onClick={confirmar ? () => setConfirmar(false) : onCerrar} disabled={enviando} style={{ ...S.btn, background: '#f3f4f6', color: '#374151' }}>{confirmar ? 'Corregir' : 'Cancelar'}</button>
        {!confirmar ? <button onClick={() => setConfirmar(true)} disabled={!hay} style={{ ...S.btn, background: '#0086ab', color: 'white', opacity: hay ? 1 : 0.5 }}>Continuar</button>
          : <button onClick={enviar} disabled={enviando} style={{ ...S.btn, background: '#16a34a', color: 'white' }}>{enviando ? 'Guardando…' : 'Confirmar y guardar'}</button>}
      </div>
    </div></div>
  );
}

// ── Tab Descuento Intellisis (C.5a-1, sólo lectura) ──────────────────────────
// Período miércoles–martes sobre api/comedor/historial; descarga un .xls real (Hoja1 importación, Hoja2 revisión).
// No escribe nada. Sin exclusiones automáticas: RH revisa la Hoja2 y retira a mano de la Hoja1 lo que no proceda.
const DIAS_ABREV = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const fmtDiaPeriodo = ymd => `${DIAS_ABREV[new Date(ymd + 'T12:00:00').getDay()]} ${fmtFechaCorta(ymd)}`;

function descargarXls(bytes, nombre) {
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/vnd.ms-excel' }));
  const a = document.createElement('a');
  a.href = url; a.download = nombre;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function TabIntellisis() {
  const [inicio, setInicio] = useState(() => periodoDe(fechaPedido(new Date().toISOString())).inicio);
  const [revision, setRevision] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');
  const [enterado, setEnterado] = useState(false);
  const { fin, dias } = periodoDe(inicio);

  useEffect(() => { setRevision(null); setError(''); setEnterado(false); }, [inicio]);

  const consultar = async () => {
    setCargando(true); setError(''); setRevision(null); setEnterado(false);
    try {
      const [h, e, p] = await Promise.all([
        apiComedor('api/comedor/historial', null, { fecha_desde: inicio, fecha_hasta: fin }),
        apiComedor('api/empleados'),
        apiComedor('api/comedor/personas').catch(() => null),
      ]);
      if (!h || !h.success) throw new Error((h && h.error) || 'No se pudo leer el historial de pedidos');
      if (!e || !Array.isArray(e.empleados)) throw new Error('No se pudo leer la lista de empleados');
      // El padrón sólo aporta nombres (con o sin la bandera activa); los consumos salen siempre de los pedidos.
      const padron = p && p.success && Array.isArray(p.personas) ? p.personas : [];
      setRevision(construirRevision({ pedidos: h.pedidos || [], empleados: e.empleados, padron, inicio }));
    } catch (err) {
      setError(err.message || 'Error de conexión');
    } finally {
      setCargando(false);
    }
  };

  const descargar = () => {
    try {
      descargarXls(bytesXls(libroIntellisis(revision, XLSX), XLSX), nombreArchivoIntellisis(inicio, fin));
    } catch (err) {
      setError(err.message);
    }
  };

  const puedeDescargar = revision && !revision.bloqueos.length && revision.filas.length > 0 && enterado;
  const caja = (fondo, borde, color) => ({ background: fondo, border: `1px solid ${borde}`, color, borderRadius: '8px', padding: '12px 14px', fontSize: '13px', marginBottom: '16px' });

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#1d3d6b' }}>Descuento Intellisis</h2>
          <p style={{ color: '#6b7280', fontSize: '13px', marginTop: '2px' }}>Período de miércoles a martes · sólo consulta</p>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <button aria-label="Período anterior" onClick={() => setInicio(sumarDias(inicio, -7))} style={{ ...S.btn, background: '#f3f4f6', padding: '6px 12px' }}>‹</button>
          <span data-testid="periodo" style={{ fontWeight: 600, color: '#1d3d6b', minWidth: '220px', textAlign: 'center' }}>{etiquetaPeriodo(inicio, fin)}</span>
          <button aria-label="Período siguiente" onClick={() => setInicio(sumarDias(inicio, 7))} style={{ ...S.btn, background: '#f3f4f6', padding: '6px 12px' }}>›</button>
          <button onClick={consultar} disabled={cargando} style={{ ...S.btn, background: '#0086ab', color: 'white', opacity: cargando ? 0.7 : 1 }}>
            {cargando ? 'Consultando…' : 'Consultar'}
          </button>
        </div>
      </div>

      {error && <div role="alert" style={caja('#fef2f2', '#fecaca', '#b91c1c')}>⚠️ {error}</div>}

      {revision && (
        <>
          <div data-testid="resumen" style={{ ...S.card, display: 'flex', gap: '28px', flexWrap: 'wrap', marginBottom: '16px', fontSize: '13px', color: '#374151' }}>
            <span><b>{revision.filas.length}</b> personas</span>
            <span><b>{revision.total}</b> comidas</span>
            <span><b>{revision.pedidosActivos}</b> pedidos activos del período</span>
            <span>{revision.cancelados} cancelados (no cuentan)</span>
            <span style={{ color: revision.total === revision.pedidosActivos ? '#16a34a' : '#b91c1c', fontWeight: 600 }}>
              {revision.total === revision.pedidosActivos ? '✓ Cuadra con los pedidos activos' : '✗ No cuadra'}
            </span>
          </div>

          {revision.bloqueos.length > 0 && (
            <div data-testid="bloqueos" style={caja('#fef2f2', '#fecaca', '#991b1b')}>
              <b>No se puede generar el archivo</b> hasta resolver lo siguiente (no se exporta nada a medias):
              <ul style={{ margin: '6px 0 0 18px' }}>
                {revision.bloqueos.map((b, i) => <li key={i}>{b.id ? `ID ${b.id}: ` : b.id_pedido ? `Pedido ${b.id_pedido}: ` : ''}{b.texto}</li>)}
              </ul>
            </div>
          )}

          {revision.avisos.length > 0 && (
            <div data-testid="avisos" style={caja('#fffbeb', '#fcd34d', '#92400e')}>
              <b>Para revisión de RH</b> (no impiden descargar):
              <ul style={{ margin: '6px 0 0 18px' }}>
                {revision.avisos.map((a, i) => <li key={i}>ID {a.id}: {a.texto}</li>)}
              </ul>
            </div>
          )}

          <div style={{ ...S.card, padding: 0, overflowX: 'auto', marginBottom: '16px' }}>
            {revision.filas.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>Sin pedidos activos en el período.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ background: '#f9fafb' }}>
                  <tr>
                    <th style={S.th}>ID</th>
                    <th style={S.th}>Nombre completo</th>
                    {dias.map(d => <th key={d} style={{ ...S.th, textAlign: 'center' }}>{fmtDiaPeriodo(d)}</th>)}
                    <th style={{ ...S.th, textAlign: 'center' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {revision.filas.map(f => (
                    <tr key={f.id}>
                      <td style={{ ...S.td, fontFamily: 'monospace' }}>{f.id}</td>
                      <td style={S.td}>
                        {f.nombre || <span style={{ color: '#b91c1c' }}>—</span>}
                        {f.origenNombre === 'PEDIDO' && <span style={{ marginLeft: '6px', fontSize: '11px', color: '#92400e' }}>(histórico)</span>}
                        {f.origenNombre === 'PADRON' && <span style={{ marginLeft: '6px', fontSize: '11px', color: '#1e40af' }}>(padrón)</span>}
                      </td>
                      {dias.map(d => <td key={d} style={{ ...S.td, textAlign: 'center', color: f.porDia[d] ? '#374151' : '#d1d5db' }}>{f.porDia[d] || '·'}</td>)}
                      <td style={{ ...S.td, textAlign: 'center', fontWeight: 700, color: '#1d3d6b' }}>{f.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {revision.filas.length > 0 && !revision.bloqueos.length && (
            <div data-testid="aviso-exclusiones" style={caja('#eff6ff', '#bfdbfe', '#1e3a8a')}>
              <b>Antes de descargar:</b> el archivo incluye a <b>todas</b> las personas con pedidos activos del período.
              No hay exclusiones automáticas: revisa la Hoja2 y retira a mano de la Hoja1 a quien no corresponda
              (fuera de nómina, bajas sin posibilidad de cobro, comidas no cobrables) antes de importar.
              La Hoja1 ya lleva la columna B vacía.
              <label style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '10px', cursor: 'pointer' }}>
                <input type="checkbox" checked={enterado} onChange={e => setEnterado(e.target.checked)} />
                Entendido: revisaré y retiraré manualmente las exclusiones.
              </label>
            </div>
          )}

          <button onClick={descargar} disabled={!puedeDescargar}
            style={{ ...S.btn, background: '#16a34a', color: 'white', display: 'flex', alignItems: 'center', gap: '6px', opacity: puedeDescargar ? 1 : 0.5, cursor: puedeDescargar ? 'pointer' : 'not-allowed' }}>
            <i className="ti ti-file-spreadsheet"></i> Descargar .xls (Excel 97-2003)
          </button>
        </>
      )}
    </div>
  );
}

function TabConfig() {
  const [config, setConfig] = useState({});
  const [editados, setEditados] = useState({});
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [msg, setMsg] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiComedor('api/comedor/config');
      if (data.success) { setConfig(data.config || {}); setEditados({}); }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const CAMPOS = [
    { clave: 'corte_diario_hora',  label: 'Hora de corte — pedido diario',   hint: 'Formato HH:MM (ej. 12:00). Pedidos antes de esta hora son para el siguiente día hábil.' },
    { clave: 'corte_semanal_hora', label: 'Hora de corte — pedido semanal',  hint: 'Formato HH:MM (ej. 10:30). Pedidos antes de esta hora son para la semana siguiente.' },
    { clave: 'dias_corte_diario',  label: 'Días de pedido diario',           hint: 'Números JS separados por coma (0=Dom, 1=Lun … 6=Sáb). Ej: 1,2,3,4,5 = Lun–Vie.' },
    { clave: 'dias_corte_semanal', label: 'Días de pedido semanal',          hint: 'Números JS separados por coma. Ej: 4,5,6 = Jue–Sáb.' },
  ];

  const valorActual = clave => (clave in editados ? editados[clave] : config[clave]) ?? '';
  const hayEdiciones = Object.keys(editados).length > 0;

  const handleGuardar = async () => {
    const claves = Object.keys(editados);
    if (claves.length === 0) { setMsg({ tipo: 'ok', texto: 'Sin cambios.' }); setTimeout(() => setMsg(null), 2000); return; }
    setGuardando(true); setMsg(null);
    let errores = 0;
    for (const clave of claves) {
      const data = await apiComedor('api/comedor/config/guardar', { clave, valor: editados[clave] });
      if (!data.success) errores++;
    }
    setGuardando(false);
    setMsg({ tipo: errores > 0 ? 'err' : 'ok', texto: errores > 0 ? `${errores} valores no se pudieron guardar.` : 'Configuración guardada.' });
    setTimeout(() => setMsg(null), 3000);
    if (errores === 0) cargar();
  };

  return (
    <div style={{ padding: '24px', maxWidth: '600px' }}>
      <div style={{ marginBottom: '20px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#1d3d6b' }}>Configuración del Comedor</h2>
        <p style={{ color: '#6b7280', fontSize: '13px', marginTop: '2px' }}>Ventanas de pedido y horarios de corte</p>
      </div>

      {msg && (
        <div style={{ marginBottom: '16px', padding: '10px 14px', borderRadius: '8px', fontSize: '13px',
          background: msg.tipo === 'err' ? '#fef2f2' : '#f0fdf4',
          color: msg.tipo === 'err' ? '#dc2626' : '#166534',
          border: `1px solid ${msg.tipo === 'err' ? '#fecaca' : '#bbf7d0'}` }}>
          {msg.tipo === 'err' ? '⚠️' : '✅'} {msg.texto}
        </div>
      )}

      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>Cargando configuración...</div>
      ) : (
        <div style={{ ...S.card, display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {CAMPOS.map(({ clave, label, hint }) => (
            <div key={clave}>
              <label style={S.label}>{label}</label>
              <input
                style={{ ...S.input, borderColor: (clave in editados) ? '#0086ab' : '#d1d5db' }}
                value={valorActual(clave)}
                onChange={e => setEditados(prev => ({ ...prev, [clave]: e.target.value }))}
              />
              <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>{hint}</div>
            </div>
          ))}

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            {hayEdiciones && (
              <button onClick={() => setEditados({})} style={{ ...S.btn, background: '#f3f4f6', color: '#374151' }}>
                Descartar cambios
              </button>
            )}
            <button onClick={handleGuardar} disabled={guardando}
              style={{ ...S.btn, background: '#0086ab', color: 'white', opacity: guardando ? 0.7 : 1 }}>
              {guardando ? 'Guardando...' : 'Guardar cambios'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Comedor (export principal) ────────────────────────────────────────────────

export function Comedor({ usuario }) {
  const [tab, setTab] = useState('pedidos');

  const TABS = [
    { id: 'pedidos',    label: 'Pedidos',            icon: 'ti-shopping-cart' },
    { id: 'menu',       label: 'Menú Semana',        icon: 'ti-calendar-week' },
    { id: 'catalogo',   label: 'Catálogo',           icon: 'ti-list' },
    { id: 'control',    label: 'Control Semanal',    icon: 'ti-chart-bar' },
    { id: 'externos',   label: 'Efectivo Externos',  icon: 'ti-cash' },
    { id: 'historial',  label: 'Historial',          icon: 'ti-history' },
    { id: 'intellisis', label: 'Descuento Intellisis', icon: 'ti-file-spreadsheet' },
    { id: 'padron',     label: 'Padrón',             icon: 'ti-address-book' },
    { id: 'config',     label: 'Configuración',      icon: 'ti-settings' },
  ];

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 700, color: '#1d3d6b' }}>
          <i className="ti ti-bowl" style={{ marginRight: '8px' }}></i>Comedor
        </h1>
        <p style={{ color: '#6b7280', fontSize: '13px', marginTop: '4px' }}>Gestión de menú, pedidos y listados del comedor</p>
      </div>
      <div style={{ display: 'flex', gap: '2px', marginBottom: '24px', borderBottom: '2px solid #e5e7eb' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 16px',
              border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 500, background: 'none',
              marginBottom: '-2px', transition: 'color 0.15s',
              borderBottom: tab === t.id ? '2px solid #0086ab' : '2px solid transparent',
              color: tab === t.id ? '#0086ab' : '#6b7280' }}>
            <i className={`ti ${t.icon}`}></i>{t.label}
          </button>
        ))}
      </div>
      {tab === 'catalogo'  && <TabCatalogo />}
      {tab === 'pedidos'   && <TabPedidos usuario={usuario} />}
      {tab === 'menu'      && <TabMenu />}
      {tab === 'control'   && <TabControlSemanal />}
      {tab === 'externos'  && <TabEfectivoExternos />}
      {tab === 'historial' && <TabHistorial />}
      {tab === 'intellisis' && <TabIntellisis />}
      {tab === 'padron'    && <TabPadron usuario={usuario} />}
      {tab === 'config'    && <TabConfig />}
    </div>
  );
}
